(function () {
  'use strict';

  const SECTION_SELECTOR = '[data-section-type="product"]';
  const sectionStateMap = new WeakMap();

  function addCleanup(state, callback) {
    if (!state || typeof callback !== 'function') return;
    state.cleanups.push(callback);
  }

  function safeClosest(event, selector) {
    const target = event ? event.target : null;

    if (target && typeof target.closest === 'function') {
      return target.closest(selector);
    }

    if (event && typeof event.composedPath === 'function') {
      const path = event.composedPath();
      for (let index = 0; index < path.length; index += 1) {
        const node = path[index];
        if (!node) continue;

        if (typeof node.matches === 'function' && node.matches(selector)) {
          return node;
        }

        if (typeof node.closest === 'function') {
          const matched = node.closest(selector);
          if (matched) return matched;
        }
      }
    }

    return null;
  }

  function formatMoney(amount) {
    if (window.Hai && typeof window.Hai.formatMoney === 'function') {
      return window.Hai.formatMoney(amount, window.Hai.settings && window.Hai.settings.moneyFormat);
    }

    const value = Number(amount) || 0;
    return `$${(value / 100).toFixed(2)}`;
  }

  function toPositiveInteger(value, fallback = 1) {
    const parsed = Number(value);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      return fallback;
    }
    return Math.max(1, Math.round(parsed));
  }

  function generateHaiGroupId() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }

    return `hai_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  }

  function parseTargetTimestamp(dateStr, timeStr) {
    if (!dateStr) return null;

    if (String(dateStr).includes('T')) {
      const isoParsed = new Date(dateStr).getTime();
      return Number.isFinite(isoParsed) ? isoParsed : null;
    }

    const normalizedTime = timeStr && /^\d{2}:\d{2}(:\d{2})?$/.test(timeStr)
      ? timeStr
      : '23:59:59';
    const withSeconds = normalizedTime.length === 5 ? `${normalizedTime}:00` : normalizedTime;
    const parsed = new Date(`${dateStr}T${withSeconds}`).getTime();

    return Number.isFinite(parsed) ? parsed : null;
  }

  function createCountdownTimer(targetTimestamp, callbacks) {
    if (!Number.isFinite(targetTimestamp)) return null;

    const safeCallbacks = callbacks || {};
    let timerId = null;

    const tick = () => {
      const diff = targetTimestamp - Date.now();
      if (diff <= 0) {
        if (timerId) {
          window.clearInterval(timerId);
          timerId = null;
        }
        if (typeof safeCallbacks.onExpired === 'function') {
          safeCallbacks.onExpired();
        }
        return;
      }

      const dayMs = 24 * 60 * 60 * 1000;
      const hourMs = 60 * 60 * 1000;
      const minuteMs = 60 * 1000;

      const days = Math.floor(diff / dayMs);
      const hours = Math.floor((diff % dayMs) / hourMs);
      const minutes = Math.floor((diff % hourMs) / minuteMs);
      const seconds = Math.floor((diff % minuteMs) / 1000);

      if (typeof safeCallbacks.onTick === 'function') {
        safeCallbacks.onTick(days, hours, minutes, seconds, diff);
      }
    };

    tick();
    timerId = window.setInterval(tick, 1000);

    return {
      destroy: () => {
        if (timerId) {
          window.clearInterval(timerId);
          timerId = null;
        }
      }
    };
  }

  function getAssociatedFormField(section, formEl, selector) {
    if (!formEl || !selector) return null;

    const selectors = String(selector)
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
    if (!selectors.length) return null;

    for (let index = 0; index < selectors.length; index += 1) {
      const matchedInForm = formEl.querySelector(selectors[index]);
      if (matchedInForm) {
        return matchedInForm;
      }
    }

    const formId = formEl.getAttribute('id');
    if (!formId) return null;

    const roots = [];
    if (section) {
      roots.push(section);
    }
    roots.push(document);

    for (let rootIndex = 0; rootIndex < roots.length; rootIndex += 1) {
      const root = roots[rootIndex];
      if (!root || typeof root.querySelector !== 'function') continue;

      for (let selectorIndex = 0; selectorIndex < selectors.length; selectorIndex += 1) {
        const scoped = root.querySelector(`${selectors[selectorIndex]}[form="${formId}"]`);
        if (scoped) {
          return scoped;
        }
      }
    }

    return null;
  }

  function resolveAssociatedVariantId(section, formEl) {
    const variantInput = getAssociatedFormField(section, formEl, 'input[name="id"], select[name="id"]');
    let variantId = toPositiveInteger(variantInput ? variantInput.value : 0, 0);
    if (variantId) return variantId;

    const variantSelect = section ? section.querySelector('[data-variant-select]') : null;
    variantId = toPositiveInteger(variantSelect ? variantSelect.value : 0, 0);
    if (variantId) return variantId;

    const productData = getProductData(section);
    if (!productData || !Array.isArray(productData.variants) || !productData.variants.length) {
      return 0;
    }

    variantId = toPositiveInteger(productData.selected_variant_id, 0);
    if (variantId) return variantId;

    return toPositiveInteger(productData.variants[0] ? productData.variants[0].id : 0, 0);
  }

  function getProductData(section, state) {
    if (state && Object.prototype.hasOwnProperty.call(state, 'productData')) {
      return state.productData;
    }

    const productJsonEl = section ? section.querySelector('[data-product-json]') : null;
    let productData = null;

    if (productJsonEl) {
      try {
        productData = JSON.parse(productJsonEl.textContent);
      } catch (error) {
        console.warn('Failed to parse product JSON', error);
      }
    }

    if (!productData || !Array.isArray(productData.variants) || !productData.variants.length) {
      productData = null;
    }

    if (state) {
      state.productData = productData;
    }

    return productData;
  }

  function initGallerySection(section, state) {
    const galleries = section.querySelectorAll('[data-product-gallery]');
    if (!galleries.length) return;

    galleries.forEach((gallery) => {
      if (!gallery || gallery.dataset.productMainGalleryBound === 'true') return;
      gallery.dataset.productMainGalleryBound = 'true';

      const wrapper = gallery.parentElement;
      const modalTemplate = wrapper ? wrapper.querySelector('[data-gallery-modal-template]') : null;
      const mainItems = Array.from(gallery.querySelectorAll('[data-gallery-item]'));
      const mainThumbs = Array.from(gallery.querySelectorAll('[data-gallery-thumb]'));
      const mainOpenButtons = Array.from(gallery.querySelectorAll('[data-gallery-open]'));
      const thumbsTrack = gallery.querySelector('[data-gallery-thumbnails-track]');
      const thumbsPrevBtn = gallery.querySelector('[data-gallery-thumbs-prev]');
      const thumbsNextBtn = gallery.querySelector('[data-gallery-thumbs-next]');
      const thumbsAreHorizontal = () => (
        gallery.dataset.thumbnailPosition === 'bottom'
        || window.matchMedia('(max-width: 767px)').matches
      );

      let modal = null;
      let closeBtn = null;
      let modalStage = null;
      let modalItems = [];
      let modalThumbs = [];
      let modalThumbsTrack = null;
      let modalThumbsPrevBtn = null;
      let modalThumbsNextBtn = null;
      let modalEventsBound = false;
      let isModalThumbsDragging = false;
      let modalThumbsDragStartX = 0;
      let modalThumbsDragStartScrollLeft = 0;
      let modalThumbsSuppressClick = false;
      let closeModalTimer = 0;

      const watchMediaLoading = (item) => {
        if (!item || item.dataset.mediaLoadingBound === 'true') return;

        item.dataset.mediaLoadingBound = 'true';
        item.setAttribute('aria-busy', 'true');

        const media = item.querySelector('img, video, iframe, model-viewer');
        if (!media) {
          item.classList.add('is-media-ready');
          item.removeAttribute('aria-busy');
          return;
        }

        let fallbackTimer = 0;
        const markReady = () => {
          if (fallbackTimer) {
            window.clearTimeout(fallbackTimer);
            fallbackTimer = 0;
          }
          item.classList.add('is-media-ready');
          item.removeAttribute('aria-busy');
        };

        const tagName = media.tagName.toLowerCase();
        const isReady = (
          (tagName === 'img' && media.complete)
          || (tagName === 'video' && media.readyState >= 2)
          || (tagName === 'model-viewer' && media.loaded)
        );

        if (isReady) {
          markReady();
          return;
        }

        const readyEvent = tagName === 'video' ? 'loadeddata' : 'load';
        media.addEventListener(readyEvent, markReady, { once: true });
        media.addEventListener('error', markReady, { once: true });
        fallbackTimer = window.setTimeout(markReady, 15000);
      };

      const watchActiveMediaLoading = (items) => {
        const activeItem = items.find((item) => item.classList.contains('is-active'));
        watchMediaLoading(activeItem);
      };

      const clearCloseModalTimer = () => {
        if (!closeModalTimer) return;
        window.clearTimeout(closeModalTimer);
        closeModalTimer = 0;
      };

      const cacheModalNodes = () => {
        if (!modal) return;
        closeBtn = modal.querySelector('[data-gallery-modal-close]');
        modalStage = modal.querySelector('.hai-product-media-modal__stage');
        modalItems = Array.from(modal.querySelectorAll('[data-gallery-modal-item]'));
        modalThumbs = Array.from(modal.querySelectorAll('[data-gallery-modal-thumb]'));
        modalThumbsTrack = modal.querySelector('[data-gallery-modal-thumbs-track]');
        modalThumbsPrevBtn = modal.querySelector('[data-gallery-modal-thumbs-prev]');
        modalThumbsNextBtn = modal.querySelector('[data-gallery-modal-thumbs-next]');
      };

      const resetModalZoom = () => {
        if (modalStage) {
          modalStage.classList.remove('is-zoomed-view');
        }

        modalItems.forEach((item) => {
          item.classList.remove('is-zoomed');
          const image = item.querySelector('img');
          if (image) {
            image.style.objectPosition = '';
          }
        });
      };

      const toggleImageZoom = (image, pointerEvent) => {
        if (!image) return;

        const stageItem = image.closest('[data-gallery-modal-item]');
        if (!stageItem || !stageItem.classList.contains('is-active')) return;

        const isZoomed = stageItem.classList.contains('is-zoomed');
        if (isZoomed) {
          stageItem.classList.remove('is-zoomed');
          if (modalStage) {
            modalStage.classList.remove('is-zoomed-view');
          }
          image.style.objectPosition = '';
          return;
        }

        resetModalZoom();

        const rect = image.getBoundingClientRect();
        if (!rect.width || !rect.height) return;

        const x = ((pointerEvent.clientX - rect.left) / rect.width) * 100;
        const y = ((pointerEvent.clientY - rect.top) / rect.height) * 100;
        const xPercent = Math.max(0, Math.min(100, x));
        const yPercent = Math.max(0, Math.min(100, y));

        image.style.objectPosition = `${xPercent}% ${yPercent}%`;
        stageItem.classList.add('is-zoomed');

        if (modalStage) {
          modalStage.classList.add('is-zoomed-view');
        }
      };

      const previewZoomPosition = (image, pointerEvent) => {
        if (!image || !pointerEvent) return;

        const stageItem = image.closest('[data-gallery-modal-item]');
        if (!stageItem || !stageItem.classList.contains('is-active') || !stageItem.classList.contains('is-zoomed')) {
          return;
        }

        const rect = image.getBoundingClientRect();
        if (!rect.width || !rect.height) return;

        const x = ((pointerEvent.clientX - rect.left) / rect.width) * 100;
        const y = ((pointerEvent.clientY - rect.top) / rect.height) * 100;
        const xPercent = Math.max(0, Math.min(100, x));
        const yPercent = Math.max(0, Math.min(100, y));

        image.style.objectPosition = `${xPercent}% ${yPercent}%`;
      };

      const getThumbScrollStep = () => {
        if (!thumbsTrack) return 96;

        const firstThumb = thumbsTrack.querySelector('.hai-product-media__thumbnail');
        if (!firstThumb) return 96;

        const styles = window.getComputedStyle(thumbsTrack);
        const gap = parseFloat(
          (thumbsAreHorizontal() ? styles.columnGap : styles.rowGap) || styles.gap || '0'
        ) || 0;
        const rect = firstThumb.getBoundingClientRect();
        return (thumbsAreHorizontal() ? rect.width : rect.height) + gap;
      };

      const updateThumbArrows = () => {
        if (!thumbsTrack || !thumbsPrevBtn || !thumbsNextBtn) return;

        const isHorizontal = thumbsAreHorizontal();
        const canScroll = isHorizontal
          ? thumbsTrack.scrollWidth > thumbsTrack.clientWidth + 2
          : thumbsTrack.scrollHeight > thumbsTrack.clientHeight + 2;

        if (!canScroll) {
          thumbsPrevBtn.hidden = true;
          thumbsNextBtn.hidden = true;
          thumbsPrevBtn.disabled = true;
          thumbsNextBtn.disabled = true;
          return;
        }

        const currentPosition = isHorizontal ? thumbsTrack.scrollLeft : thumbsTrack.scrollTop;
        const maxPosition = isHorizontal
          ? thumbsTrack.scrollWidth - thumbsTrack.clientWidth
          : thumbsTrack.scrollHeight - thumbsTrack.clientHeight;
        const isAtStart = currentPosition <= 1;
        const isAtEnd = currentPosition >= maxPosition - 1;

        // 缩略图到达边界时隐藏无效方向的按钮，初始状态只显示下一页箭头。
        thumbsPrevBtn.hidden = isAtStart;
        thumbsNextBtn.hidden = isAtEnd;
        thumbsPrevBtn.disabled = isAtStart;
        thumbsNextBtn.disabled = isAtEnd;
      };

      const scrollThumbs = (direction) => {
        if (!thumbsTrack) return;

        const step = getThumbScrollStep();
        const isHorizontal = thumbsAreHorizontal();
        const viewportSize = isHorizontal ? thumbsTrack.clientWidth : thumbsTrack.clientHeight;
        const distance = Math.max(step * 2, viewportSize * 0.72);
        const currentPosition = isHorizontal ? thumbsTrack.scrollLeft : thumbsTrack.scrollTop;
        const maxPosition = isHorizontal
          ? thumbsTrack.scrollWidth - thumbsTrack.clientWidth
          : thumbsTrack.scrollHeight - thumbsTrack.clientHeight;
        let targetPosition = Math.max(0, Math.min(maxPosition, currentPosition + (distance * direction)));

        if (direction < 0 && targetPosition <= step) {
          targetPosition = 0;
        } else if (direction > 0 && maxPosition - targetPosition <= step) {
          targetPosition = maxPosition;
        }

        if (isHorizontal) {
          thumbsTrack.scrollTo({ left: targetPosition, behavior: 'smooth' });
        } else {
          thumbsTrack.scrollTo({ top: targetPosition, behavior: 'smooth' });
        }

        window.setTimeout(updateThumbArrows, 220);
      };

      const getModalThumbScrollStep = () => {
        if (!modalThumbsTrack) return 100;

        const firstThumb = modalThumbsTrack.querySelector('.hai-product-media-modal__thumb');
        if (!firstThumb) return 100;

        const styles = window.getComputedStyle(modalThumbsTrack);
        const gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
        return firstThumb.getBoundingClientRect().width + gap;
      };

      const updateModalThumbArrows = () => {
        if (!modal || !modalThumbsTrack || !modalThumbsPrevBtn || !modalThumbsNextBtn) return;

        if (modal.hidden) {
          return;
        }

        const canScroll = modalThumbsTrack.scrollWidth > modalThumbsTrack.clientWidth + 2;

        if (!canScroll) {
          modalThumbsPrevBtn.hidden = true;
          modalThumbsNextBtn.hidden = true;
          modalThumbsPrevBtn.disabled = true;
          modalThumbsNextBtn.disabled = true;
          return;
        }

        const atStart = modalThumbsTrack.scrollLeft <= 1;
        const atEnd = modalThumbsTrack.scrollLeft + modalThumbsTrack.clientWidth >= modalThumbsTrack.scrollWidth - 1;

        modalThumbsPrevBtn.hidden = atStart;
        modalThumbsNextBtn.hidden = atEnd;
        modalThumbsPrevBtn.disabled = atStart;
        modalThumbsNextBtn.disabled = atEnd;
      };

      const scrollModalThumbs = (direction) => {
        if (!modalThumbsTrack) return;

        const step = getModalThumbScrollStep();
        const distance = Math.max(step * 3, modalThumbsTrack.clientWidth * 0.72);

        modalThumbsTrack.scrollBy({
          left: distance * direction,
          behavior: 'smooth'
        });

        window.setTimeout(updateModalThumbArrows, 220);
      };

      const onModalThumbsMouseDown = (event) => {
        if (!modalThumbsTrack || event.button !== 0) return;

        isModalThumbsDragging = true;
        modalThumbsDragStartX = event.clientX;
        modalThumbsDragStartScrollLeft = modalThumbsTrack.scrollLeft;
        modalThumbsSuppressClick = false;
        modalThumbsTrack.classList.add('is-dragging');
      };

      const onDocumentMouseMove = (event) => {
        if (!isModalThumbsDragging || !modalThumbsTrack) return;

        const deltaX = event.clientX - modalThumbsDragStartX;
        if (Math.abs(deltaX) > 4) {
          modalThumbsSuppressClick = true;
        }

        modalThumbsTrack.scrollLeft = modalThumbsDragStartScrollLeft - deltaX;
        updateModalThumbArrows();
      };

      const onDocumentMouseUp = () => {
        if (!isModalThumbsDragging || !modalThumbsTrack) return;

        isModalThumbsDragging = false;
        modalThumbsTrack.classList.remove('is-dragging');

        if (modalThumbsSuppressClick) {
          window.setTimeout(() => {
            modalThumbsSuppressClick = false;
          }, 0);
        }
      };

      const applyState = (items, thumbs, mediaId) => {
        if (!items.length) return null;

        const targetId = String(mediaId);
        let activeId = null;

        items.forEach((item) => {
          const isMatch = item.dataset.mediaId === targetId;
          item.classList.toggle('is-active', isMatch);
          item.hidden = !isMatch;
          if (isMatch) activeId = item.dataset.mediaId;
        });

        if (!activeId && items[0]) {
          activeId = items[0].dataset.mediaId;
          items[0].classList.add('is-active');
          items[0].hidden = false;
        }

        thumbs.forEach((thumb) => {
          const isMatch = activeId && thumb.dataset.mediaId === activeId;
          thumb.classList.toggle('is-active', isMatch);
        });

        if (thumbsTrack && thumbs === mainThumbs) {
          const activeThumb = thumbsTrack.querySelector('.hai-product-media__thumbnail.is-active');
          if (activeThumb && typeof activeThumb.scrollIntoView === 'function') {
            activeThumb.scrollIntoView({ block: 'nearest', inline: 'nearest' });
          }
          updateThumbArrows();
        }

        if (modalThumbsTrack && thumbs === modalThumbs) {
          const activeModalThumb = modalThumbsTrack.querySelector('.hai-product-media-modal__thumb.is-active');
          if (activeModalThumb && typeof activeModalThumb.scrollIntoView === 'function') {
            activeModalThumb.scrollIntoView({ block: 'nearest', inline: 'center' });
          }
          updateModalThumbArrows();
        }

        return activeId;
      };

      const createModalFromTemplate = () => {
        if (!modalTemplate) return null;

        if (modalTemplate.content && modalTemplate.content.firstElementChild) {
          return modalTemplate.content.firstElementChild.cloneNode(true);
        }

        const tempContainer = document.createElement('div');
        tempContainer.innerHTML = (modalTemplate.innerHTML || '').trim();
        return tempContainer.firstElementChild;
      };

      const bindModalEvents = () => {
        if (!modal || modalEventsBound) return;

        if (modalThumbsPrevBtn) {
          modalThumbsPrevBtn.addEventListener('click', onModalThumbPrevClick);
        }

        if (modalThumbsNextBtn) {
          modalThumbsNextBtn.addEventListener('click', onModalThumbNextClick);
        }

        if (modalThumbsTrack) {
          modalThumbsTrack.addEventListener('mousedown', onModalThumbsMouseDown);
          modalThumbsTrack.addEventListener('scroll', updateModalThumbArrows, { passive: true });
        }

        modal.addEventListener('click', onModalClick);
        modal.addEventListener('mousemove', onModalMouseMove);

        if (closeBtn) {
          closeBtn.addEventListener('click', onCloseClick);
        }

        modalEventsBound = true;
      };

      const unbindModalEvents = () => {
        if (!modal || !modalEventsBound) return;

        if (modalThumbsPrevBtn) {
          modalThumbsPrevBtn.removeEventListener('click', onModalThumbPrevClick);
        }

        if (modalThumbsNextBtn) {
          modalThumbsNextBtn.removeEventListener('click', onModalThumbNextClick);
        }

        if (modalThumbsTrack) {
          modalThumbsTrack.removeEventListener('mousedown', onModalThumbsMouseDown);
          modalThumbsTrack.removeEventListener('scroll', updateModalThumbArrows);
        }

        modal.removeEventListener('click', onModalClick);
        modal.removeEventListener('mousemove', onModalMouseMove);

        if (closeBtn) {
          closeBtn.removeEventListener('click', onCloseClick);
        }

        modalEventsBound = false;
      };

      const ensureModal = () => {
        if (modal) return modal;

        const nextModal = createModalFromTemplate();
        if (!nextModal) return null;

        modal = nextModal;
        modal.hidden = true;
        document.body.appendChild(modal);
        cacheModalNodes();
        bindModalEvents();

        const activeMainItem = gallery.querySelector('[data-gallery-item].is-active');
        const activeMediaId = activeMainItem
          ? activeMainItem.dataset.mediaId
          : (mainItems[0] ? mainItems[0].dataset.mediaId : null);

        applyState(modalItems, modalThumbs, activeMediaId);
        watchActiveMediaLoading(modalItems);
        updateModalThumbArrows();

        return modal;
      };

      const setActiveMedia = (mediaId) => {
        const fallbackId = mainItems[0] ? mainItems[0].dataset.mediaId : null;
        const targetId = mediaId || fallbackId;

        if (!targetId) return;

        resetModalZoom();
        const activeMainId = applyState(mainItems, mainThumbs, targetId) || fallbackId;
        watchActiveMediaLoading(mainItems);
        if (modal) {
          applyState(modalItems, modalThumbs, activeMainId);
          watchActiveMediaLoading(modalItems);
        }
      };

      const openModal = (mediaId) => {
        const ensuredModal = ensureModal();
        if (!ensuredModal) return;

        clearCloseModalTimer();
        setActiveMedia(mediaId);
        ensuredModal.hidden = false;
        modalThumbsPrevBtn.hidden = true;
        modalThumbsPrevBtn.disabled = true;
        modalThumbsNextBtn.hidden = false;
        modalThumbsNextBtn.disabled = false;
        window.requestAnimationFrame(() => {
          ensuredModal.classList.add('is-open');
          window.requestAnimationFrame(updateModalThumbArrows);
        });
        window.setTimeout(updateModalThumbArrows, 220);
        document.body.classList.add('hai-overflow-hidden');
      };

      const closeModal = (immediate = false) => {
        if (!modal) return;

        clearCloseModalTimer();
        resetModalZoom();
        modal.classList.remove('is-open');
        document.body.classList.remove('hai-overflow-hidden');

        if (immediate) {
          modal.hidden = true;
          return;
        }

        closeModalTimer = window.setTimeout(() => {
          closeModalTimer = 0;
          if (modal && !modal.classList.contains('is-open')) {
            modal.hidden = true;
          }
        }, 180);
      };

      const onGalleryClick = (event) => {
        const thumb = safeClosest(event, '[data-gallery-thumb]');
        if (thumb) {
          event.preventDefault();
          setActiveMedia(thumb.dataset.mediaId);
          return;
        }

        const openBtn = safeClosest(event, '[data-gallery-open]');
        if (openBtn) {
          event.preventDefault();
          const mediaItem = openBtn.closest('[data-gallery-item]');
          openModal(mediaItem ? mediaItem.dataset.mediaId : null);
          return;
        }

        const mediaItem = safeClosest(event, '[data-gallery-item]');
        if (mediaItem) {
          event.preventDefault();
          openModal(mediaItem.dataset.mediaId);
        }
      };

      const onMainOpenButtonClick = (event) => {
        event.preventDefault();
        event.stopPropagation();
        const mediaItem = event.currentTarget.closest('[data-gallery-item]');
        openModal(mediaItem ? mediaItem.dataset.mediaId : null);
      };

      const onThumbPrevClick = (event) => {
        event.preventDefault();
        scrollThumbs(-1);
      };

      const onThumbNextClick = (event) => {
        event.preventDefault();
        scrollThumbs(1);
      };

      const onModalThumbPrevClick = (event) => {
        event.preventDefault();
        scrollModalThumbs(-1);
      };

      const onModalThumbNextClick = (event) => {
        event.preventDefault();
        scrollModalThumbs(1);
      };

      const onModalClick = (event) => {
        if (!modal) return;

        if (event.target === modal) {
          closeModal();
          return;
        }

        const modalThumb = safeClosest(event, '[data-gallery-modal-thumb]');
        if (modalThumb) {
          if (modalThumbsSuppressClick) {
            event.preventDefault();
            return;
          }
          event.preventDefault();
          setActiveMedia(modalThumb.dataset.mediaId);
          return;
        }

        const activeImage = safeClosest(event, '.hai-product-media-modal__stage-item.is-active img');
        if (activeImage) {
          event.preventDefault();
          toggleImageZoom(activeImage, event);
        }
      };

      const onModalMouseMove = (event) => {
        const activeZoomedImage = safeClosest(event, '.hai-product-media-modal__stage-item.is-zoomed img');
        if (!activeZoomedImage) return;
        previewZoomPosition(activeZoomedImage, event);
      };

      const onCloseClick = (event) => {
        event.preventDefault();
        closeModal();
      };

      const onDocumentKeydown = (event) => {
        if (event.key === 'Escape' && modal && !modal.hidden) {
          closeModal();
        }
      };

      const onWindowResize = () => {
        updateThumbArrows();
        updateModalThumbArrows();
      };

      gallery.addEventListener('click', onGalleryClick);
      mainOpenButtons.forEach((openBtn) => {
        openBtn.addEventListener('click', onMainOpenButtonClick);
      });

      if (thumbsPrevBtn) {
        thumbsPrevBtn.addEventListener('click', onThumbPrevClick);
      }

      if (thumbsNextBtn) {
        thumbsNextBtn.addEventListener('click', onThumbNextClick);
      }

      if (thumbsTrack) {
        thumbsTrack.addEventListener('scroll', updateThumbArrows, { passive: true });
      }

      window.addEventListener('resize', onWindowResize);

      document.addEventListener('mousemove', onDocumentMouseMove);
      document.addEventListener('mouseup', onDocumentMouseUp);

      document.addEventListener('keydown', onDocumentKeydown);

      setActiveMedia(mainItems[0] ? mainItems[0].dataset.mediaId : null);
      updateThumbArrows();

      addCleanup(state, () => {
        gallery.removeEventListener('click', onGalleryClick);

        mainOpenButtons.forEach((openBtn) => {
          openBtn.removeEventListener('click', onMainOpenButtonClick);
        });

        if (thumbsPrevBtn) {
          thumbsPrevBtn.removeEventListener('click', onThumbPrevClick);
        }

        if (thumbsNextBtn) {
          thumbsNextBtn.removeEventListener('click', onThumbNextClick);
        }

        if (thumbsTrack) {
          thumbsTrack.removeEventListener('scroll', updateThumbArrows);
        }

        window.removeEventListener('resize', onWindowResize);

        document.removeEventListener('mousemove', onDocumentMouseMove);
        document.removeEventListener('mouseup', onDocumentMouseUp);

        document.removeEventListener('keydown', onDocumentKeydown);
        clearCloseModalTimer();
        document.body.classList.remove('hai-overflow-hidden');

        if (modal) {
          closeModal(true);
          unbindModalEvents();

          if (modal.parentNode) {
            modal.parentNode.removeChild(modal);
          }

          modal = null;
          closeBtn = null;
          modalStage = null;
          modalItems = [];
          modalThumbs = [];
          modalThumbsTrack = null;
          modalThumbsPrevBtn = null;
          modalThumbsNextBtn = null;
        }

        delete gallery.dataset.productMainGalleryBound;
      });
    });
  }

  function initVariantSection(section, state) {
    const productData = getProductData(section, state);
    if (!productData) return;
    const optionNames = Array.isArray(productData.option_names) ? productData.option_names : [];
    const defaultSelectedOptions = Array.isArray(productData.selected_options) ? productData.selected_options : [];

    const productForm = section.querySelector('[data-product-form]');
    const formEl = productForm
      ? productForm.querySelector('form')
      : section.querySelector('form[action*="/cart/add"]');
    const idInput = formEl
      ? getAssociatedFormField(section, formEl, 'input[name="id"], select[name="id"]')
      : null;

    const variantSelect = section.querySelector('[data-variant-select]');
    const variantRadios = Array.from(section.querySelectorAll('[data-variant-option]'));

    const colorOptionFieldset = section.querySelector('[data-color-option="true"]');
    const colorLegendValue = colorOptionFieldset
      ? colorOptionFieldset.querySelector('[data-color-legend-value]')
      : null;
    const colorOptionPosition = colorOptionFieldset
      ? Number(colorOptionFieldset.dataset.optionPosition)
      : 0;

    const priceContainer = section.querySelector('[data-product-price]');
    const currentPriceEl = priceContainer ? priceContainer.querySelector('[data-product-price-current]') : null;
    const comparePriceEl = priceContainer ? priceContainer.querySelector('[data-product-price-compare]') : null;
    const badgeEl = priceContainer ? priceContainer.querySelector('[data-product-price-badge]') : null;

    const addToCartButton = section.querySelector('[data-add-to-cart]');
    if (addToCartButton && !addToCartButton.dataset.defaultLabel) {
      addToCartButton.dataset.defaultLabel = (addToCartButton.textContent || '').trim();
    }

    const gallery = section.querySelector('[data-product-gallery]');
    const galleryItems = gallery ? Array.from(gallery.querySelectorAll('[data-gallery-item]')) : [];
    const galleryThumbs = gallery ? Array.from(gallery.querySelectorAll('[data-gallery-thumb]')) : [];

    const applyMediaState = (items, thumbs, mediaId) => {
      if (!items.length) return false;

      let matched = false;
      const targetId = String(mediaId);

      items.forEach((item) => {
        const isMatch = item.dataset.mediaId === targetId;
        item.classList.toggle('is-active', isMatch);
        item.hidden = !isMatch;
        if (isMatch) matched = true;
      });

      if (!matched) {
        items.forEach((item, index) => {
          const isFirst = index === 0;
          item.classList.toggle('is-active', isFirst);
          item.hidden = !isFirst;
        });
      }

      if (thumbs.length) {
        thumbs.forEach((thumb, index) => {
          const isMatch = matched ? thumb.dataset.mediaId === targetId : index === 0;
          thumb.classList.toggle('is-active', isMatch);
        });
      }

      return matched;
    };

    const setActiveMedia = (mediaId) => {
      const fallbackMediaId =
        mediaId ||
        section.querySelector('[data-gallery-item].is-active')?.dataset.mediaId ||
        galleryItems[0]?.dataset.mediaId;

      if (!fallbackMediaId) return;
      applyMediaState(galleryItems, galleryThumbs, fallbackMediaId);
    };

    const updatePriceDisplay = (variant) => {
      if (!priceContainer || !variant) return;

      if (currentPriceEl) {
        currentPriceEl.textContent = formatMoney(variant.price);
        currentPriceEl.dataset.price = variant.price;
      }

      if (comparePriceEl) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          comparePriceEl.hidden = false;
          comparePriceEl.textContent = formatMoney(variant.compare_at_price);
          comparePriceEl.dataset.price = variant.compare_at_price;
        } else {
          comparePriceEl.hidden = true;
          comparePriceEl.dataset.price = '';
        }
      }

      if (badgeEl) {
        badgeEl.hidden = true;
        badgeEl.dataset.status = 'available';

        if (!variant.available) {
          badgeEl.hidden = false;
          badgeEl.dataset.status = 'sold_out';
          badgeEl.textContent = (window.Hai && window.Hai.strings && window.Hai.strings.soldOut) || 'Sold out';
        } else if (variant.inventory_policy === 'continue') {
          badgeEl.hidden = false;
          badgeEl.dataset.status = 'preorder';
          badgeEl.textContent = (window.Hai && window.Hai.strings && window.Hai.strings.preOrder) || 'Pre-order';
        }
      }
    };

    const updateAddToCartState = (variant) => {
      if (!addToCartButton) return;

      const addLabel = addToCartButton.dataset.defaultLabel
        || (window.Hai && window.Hai.strings && window.Hai.strings.addToCart)
        || 'Add to cart';
      const soldOutLabel = (window.Hai && window.Hai.strings && window.Hai.strings.soldOut) || 'Sold out';

      if (!variant || !variant.available) {
        addToCartButton.disabled = true;
        addToCartButton.setAttribute('aria-disabled', 'true');
        addToCartButton.textContent = soldOutLabel;
      } else {
        addToCartButton.disabled = false;
        addToCartButton.setAttribute('aria-disabled', 'false');
        addToCartButton.textContent = addLabel;
      }
    };

    const syncRadios = (variant) => {
      if (!variantRadios.length || !variant) return;

      variantRadios.forEach((radio) => {
        const position = Number(radio.dataset.optionPosition);
        if (!position) return;

        const variantValue = variant.options[position - 1];
        if (variantValue && radio.value === variantValue) {
          radio.checked = true;
        }
      });
    };

    const getCurrentOptions = () => {
      const selectedOptions = [];

      for (let index = 0; index < optionNames.length; index += 1) {
        const position = index + 1;
        const selectedRadio = section.querySelector(`[data-variant-option][data-option-position="${position}"]:checked`);

        if (selectedRadio) {
          selectedOptions.push(selectedRadio.value);
          continue;
        }

        if (variantSelect && variantSelect.options.length) {
          const selectedVariant = productData.variants.find(
            (variant) => Number(variantSelect.value) === Number(variant.id)
          );

          if (selectedVariant) {
            selectedOptions.push(selectedVariant.options[index]);
            continue;
          }
        }

        selectedOptions.push(defaultSelectedOptions[index] || '');
      }

      return selectedOptions;
    };

    const findVariant = (options) => {
      if (!Array.isArray(options)) return null;
      return productData.variants.find((variant) => {
        return variant.options.every((optionValue, idx) => optionValue === options[idx]);
      });
    };

    const updateColorOptionAvailability = (selectedOptions = null) => {
      if (!variantRadios.length) return;

      const activeOptions = Array.isArray(selectedOptions) && selectedOptions.length
        ? selectedOptions.slice()
        : getCurrentOptions();

      variantRadios.forEach((radio) => {
        if (!radio || !radio.classList) return;

        const label = radio.nextElementSibling;
        if (!label || !label.classList || !label.classList.contains('hai-variant-label--color')) {
          return;
        }

        const position = Number(radio.dataset.optionPosition);
        if (!position) return;

        const candidateOptions = activeOptions.slice();
        candidateOptions[position - 1] = radio.value;

        const hasAvailableVariant = productData.variants.some((variant) => {
          const isMatchingVariant = variant.options.every((optionValue, optionIndex) => {
            return optionValue === candidateOptions[optionIndex];
          });

          if (!isMatchingVariant) return false;

          return variant && variant.available !== false;
        });

        label.classList.toggle('is-unavailable', !hasAvailableVariant);
        label.removeAttribute('aria-disabled');
      });
    };

    const updateColorLegend = (variant, selectedOptions = null) => {
      if (!colorLegendValue || !colorOptionPosition) return;

      let nextValue = '';

      if (variant && Array.isArray(variant.options)) {
        nextValue = variant.options[colorOptionPosition - 1] || '';
      } else if (Array.isArray(selectedOptions)) {
        nextValue = selectedOptions[colorOptionPosition - 1] || '';
      } else {
        const checkedColor = section.querySelector(
          `[data-variant-option][data-option-position="${colorOptionPosition}"]:checked`
        );
        nextValue = checkedColor ? checkedColor.value : '';
      }

      if (nextValue) {
        colorLegendValue.textContent = nextValue;
      }
    };

    const inventoryModule = section.querySelector('[data-inventory-module]');
    const inventoryWrapper = inventoryModule ? inventoryModule.querySelector('.inventory-display-wrapper-v2') : null;
    const inventoryMessage = inventoryModule ? inventoryModule.querySelector('[data-inventory-message]') : null;
    const inventoryMessageText = inventoryModule ? inventoryModule.querySelector('[data-inventory-message-text]') : null;
    const inventoryDot = inventoryModule ? inventoryModule.querySelector('[data-inventory-dot]') : null;
    const inventorySubtext = inventoryModule ? inventoryModule.querySelector('[data-inventory-subtext]') : null;
    const inventoryProgressFill = inventoryModule ? inventoryModule.querySelector('[data-inventory-progress-fill]') : null;
    const inventoryStatusClasses = [
      'inventory-display-wrapper-v2--preorder',
      'inventory-display-wrapper-v2--out_of_stock',
      'inventory-display-wrapper-v2--low_stock',
      'inventory-display-wrapper-v2--selling_fast',
      'inventory-display-wrapper-v2--in_stock',
      'inventory-display-wrapper-v2--demand_based'
    ];

    const updateInventoryDisplay = (variant) => {
      if (!inventoryModule || !inventoryWrapper || !inventoryMessage || !inventoryMessageText || !inventoryDot || !inventorySubtext || !inventoryProgressFill) {
        return;
      }

      const displayMode = inventoryModule.dataset.displayMode || 'real';
      const lowStockThreshold = Number(inventoryModule.dataset.lowStockThreshold) || 10;
      const mediumStockThreshold = Number(inventoryModule.dataset.mediumStockThreshold) || 50;
      const sold24h = Number(inventoryModule.dataset.sold24h) || 0;
      const hotLevel = (inventoryModule.dataset.hotLevel || '').toLowerCase();
      const restockedLabel = inventoryModule.dataset.restockedLabel || '';
      const fontSize = Number(inventoryModule.dataset.fontSize) || 14;
      const textColor = inventoryModule.dataset.textColor || '#1f2937';

      let status = 'hidden';
      let primaryMessage = '';
      let secondaryMessage = '';
      let showBlink = false;
      let iconColor = '#ef4444';
      let progressStartColor = '#f87171';
      let progressEndColor = '#dc2626';
      let progressPercent = 100;

      if (variant) {
        const rawInventoryQuantity = variant.inventory_quantity;
        const hasInventoryQuantity = rawInventoryQuantity !== null && rawInventoryQuantity !== undefined && rawInventoryQuantity !== '';
        const variantInventory = hasInventoryQuantity ? Number(rawInventoryQuantity) : 0;
        const variantAvailable = variant.available !== false;
        const inventoryManagement = variant.inventory_management || '';
        const inventoryPolicy = variant.inventory_policy || 'deny';

        if (inventoryManagement) {
          if (hasInventoryQuantity) {
            if (variantInventory <= 0) {
              if (inventoryPolicy === 'continue') {
                status = 'preorder';
                primaryMessage = 'Popular item - pre-order now to secure delivery';
                iconColor = '#2563eb';
                progressStartColor = '#60a5fa';
                progressEndColor = '#2563eb';
                progressPercent = 100;
              } else {
                status = 'out_of_stock';
                primaryMessage = 'Out of stock - check back soon';
                iconColor = '#94a3b8';
                progressStartColor = '#cbd5e1';
                progressEndColor = '#94a3b8';
                progressPercent = 0;
              }
            } else if (variantInventory <= lowStockThreshold) {
              status = 'low_stock';
              primaryMessage = `Hurry, only ${variantInventory} items left in stock!`;
              showBlink = true;
              iconColor = '#ef4444';
              progressStartColor = '#f87171';
              progressEndColor = '#dc2626';
              progressPercent = 22;
            } else if (variantInventory <= mediumStockThreshold) {
              status = 'selling_fast';
              primaryMessage = 'Selling fast - limited stock available';
              iconColor = '#f59e0b';
              progressStartColor = '#fbbf24';
              progressEndColor = '#f59e0b';
              progressPercent = 58;
              if (sold24h > 0) {
                secondaryMessage = `${sold24h} sold in the last 24 hours`;
              }
            } else {
              status = 'in_stock';
              primaryMessage = 'In stock - ships within 24 hours';
              iconColor = '#16a34a';
              progressStartColor = '#4ade80';
              progressEndColor = '#16a34a';
              progressPercent = 88;
              if (restockedLabel) {
                secondaryMessage = restockedLabel;
              } else if (sold24h > 0) {
                secondaryMessage = `${sold24h} sold in the last 24 hours`;
              }
            }
          } else {
            if (variantAvailable) {
              status = 'in_stock';
              primaryMessage = 'In stock - ships within 24 hours';
              iconColor = '#16a34a';
              progressStartColor = '#4ade80';
              progressEndColor = '#16a34a';
              progressPercent = 88;
              if (restockedLabel) {
                secondaryMessage = restockedLabel;
              } else if (sold24h > 0) {
                secondaryMessage = `${sold24h} sold in the last 24 hours`;
              }
            } else if (inventoryPolicy === 'continue') {
              status = 'preorder';
              primaryMessage = 'Popular item - pre-order now to secure delivery';
              iconColor = '#2563eb';
              progressStartColor = '#60a5fa';
              progressEndColor = '#2563eb';
              progressPercent = 100;
            } else {
              status = 'out_of_stock';
              primaryMessage = 'Out of stock - check back soon';
              iconColor = '#94a3b8';
              progressStartColor = '#cbd5e1';
              progressEndColor = '#94a3b8';
              progressPercent = 0;
            }
          }
        } else {
          status = 'demand_based';
          if (sold24h > 0) {
            primaryMessage = `${sold24h} sold in the last 24 hours`;
            secondaryMessage = 'Trending now - high demand';
            iconColor = '#f59e0b';
            progressStartColor = '#fbbf24';
            progressEndColor = '#f59e0b';
            progressPercent = 65;
          } else if (hotLevel.includes('high') || hotLevel.includes('hot') || hotLevel.includes('trend')) {
            primaryMessage = 'Trending now - high demand';
            iconColor = '#f59e0b';
            progressStartColor = '#fbbf24';
            progressEndColor = '#f59e0b';
            progressPercent = 62;
          } else {
            primaryMessage = 'In stock - ready to ship';
            iconColor = '#16a34a';
            progressStartColor = '#4ade80';
            progressEndColor = '#16a34a';
            progressPercent = 85;
          }
        }
      }

      if (variant && (variant.presale_enabled === true || variant.presale_enabled === 'true')) {
        status = 'preorder';
        primaryMessage = variant.presale_ship_text
          ? `Pre-order · ${variant.presale_ship_text}`
          : 'Pre-order now';
        secondaryMessage = variant.presale_note || '';
        showBlink = false;
        iconColor = '#2563eb';
        progressStartColor = '#60a5fa';
        progressEndColor = '#2563eb';
        progressPercent = 100;
      }

      if (displayMode === 'marketing' && status === 'in_stock') {
        primaryMessage = 'Best seller - stock moves fast';
        secondaryMessage = '';
        iconColor = '#f59e0b';
        progressStartColor = '#fbbf24';
        progressEndColor = '#f59e0b';
        progressPercent = 64;
      }

      if (status === 'hidden' || !primaryMessage) {
        inventoryModule.hidden = true;
        return;
      }

      inventoryModule.hidden = false;
      inventoryStatusClasses.forEach((className) => inventoryWrapper.classList.remove(className));
      inventoryWrapper.classList.add(`inventory-display-wrapper-v2--${status}`);

      inventoryMessage.style.fontSize = `${fontSize}px`;
      inventoryMessage.style.color = textColor;
      inventoryMessageText.textContent = primaryMessage;

      inventoryDot.classList.toggle('inventory-dot--blink', showBlink);
      inventoryDot.style.backgroundColor = iconColor;

      if (secondaryMessage) {
        inventorySubtext.hidden = false;
        inventorySubtext.textContent = secondaryMessage;
      } else {
        inventorySubtext.hidden = true;
        inventorySubtext.textContent = '';
      }

      inventoryProgressFill.style.width = `${progressPercent}%`;
      inventoryProgressFill.style.background = `linear-gradient(90deg, ${progressStartColor} 0%, ${progressEndColor} 100%)`;
    };

    const dispatchUiUpdated = () => {
      section.dispatchEvent(new CustomEvent('hai:product:ui-updated'));
    };

    const setVariant = (variant, options = {}) => {
      if (!variant) return;
      const nextVariant = variant;

      if (variantSelect) {
        variantSelect.value = String(nextVariant.id);
      }

      if (idInput) {
        idInput.value = nextVariant.id;
      }

      updatePriceDisplay(nextVariant);
      updateAddToCartState(nextVariant);
      updateColorLegend(nextVariant);
      updateInventoryDisplay(nextVariant);
      updateColorOptionAvailability(nextVariant.options);

      if (!options.skipMediaUpdate && nextVariant.featured_media_id) {
        setActiveMedia(nextVariant.featured_media_id);
      }

      dispatchUiUpdated();
    };

    const handleVariantChange = () => {
      const options = getCurrentOptions();
      const variant = findVariant(options);

      if (!variant) {
        updateColorLegend(null, options);
        updateInventoryDisplay(null);
        updateColorOptionAvailability(options);

        if (addToCartButton) {
          addToCartButton.disabled = true;
          addToCartButton.setAttribute('aria-disabled', 'true');
          addToCartButton.textContent = (window.Hai && window.Hai.strings && window.Hai.strings.selectVariant)
            || 'Select a variant';
        }

        dispatchUiUpdated();
        return;
      }

      setVariant(variant);
    };

    variantRadios.forEach((radio) => {
      radio.addEventListener('change', handleVariantChange);
      addCleanup(state, () => {
        radio.removeEventListener('change', handleVariantChange);
      });
    });

    if (variantSelect) {
      const onVariantSelectChange = (event) => {
        const selectedVariant = productData.variants.find(
          (variant) => Number(variant.id) === Number(event.target.value)
        );

        if (!selectedVariant) return;

        syncRadios(selectedVariant);
        setVariant(selectedVariant);
      };

      variantSelect.addEventListener('change', onVariantSelectChange);
      addCleanup(state, () => {
        variantSelect.removeEventListener('change', onVariantSelectChange);
      });
    }

    const initialVariantId = Number(
      variantSelect
        ? variantSelect.value
        : productData.selected_variant_id
    );

    const initialVariant = productData.variants.find(
      (variant) => Number(variant.id) === initialVariantId
    ) || productData.variants[0];

    if (initialVariant) {
      syncRadios(initialVariant);
      setVariant(initialVariant, { skipMediaUpdate: true });
    }
  }

  function initBundleSection(section, state) {
    const bundleOffer = section.querySelector('[data-bundle-offer]');
    if (!bundleOffer || bundleOffer.dataset.productMainBundleBound === 'true') return;
    bundleOffer.dataset.productMainBundleBound = 'true';

    const bars = Array.from(bundleOffer.querySelectorAll('[data-bundle-bar]'));
    if (!bars.length) return;

    const productData = getProductData(section, state);
    if (!productData) return;

    const formEl = section.querySelector('[data-product-form] form') || section.querySelector('form[action*="/cart/add"]');
    if (!formEl) return;

    const variantInput = getAssociatedFormField(section, formEl, 'input[name="id"], select[name="id"]');
    const defaultOfferId = bundleOffer.dataset.defaultOffer || '';
    const savedLabelTemplate = bundleOffer.dataset.savedLabelTemplate || "YOU'RE SAVING [saved_total]";

    const formId = formEl.getAttribute('id');

    let quantityInput = formEl.querySelector('input[name="quantity"]');
    if (!quantityInput && formId) {
      quantityInput = section.querySelector(`input[name="quantity"][form="${formId}"]`);
    }

    let quantityInputCreated = false;
    if (!quantityInput) {
      quantityInput = document.createElement('input');
      quantityInput.type = 'hidden';
      quantityInput.name = 'quantity';
      quantityInput.value = '1';
      formEl.appendChild(quantityInput);
      quantityInputCreated = true;
    }

    let propertyInput = bundleOffer.querySelector('[data-bundle-property-input]')
      || formEl.querySelector('input[name="properties[_bundle_offer]"]');
    let propertyInputCreated = false;

    if (!propertyInput) {
      propertyInput = document.createElement('input');
      propertyInput.type = 'hidden';
      propertyInput.name = 'properties[_bundle_offer]';
      propertyInput.value = '';
      formEl.appendChild(propertyInput);
      propertyInputCreated = true;
    }

    const getVariantById = (variantId) => {
      const normalizedId = Number(variantId);
      if (!normalizedId) return null;
      return productData.variants.find((variant) => Number(variant.id) === normalizedId) || null;
    };

    const getCurrentVariant = () => {
      const currentVariantFromInput = variantInput ? getVariantById(variantInput.value) : null;
      if (currentVariantFromInput) return currentVariantFromInput;

      const selectedVariantId = productData.selected_variant_id || null;

      return getVariantById(selectedVariantId) || productData.variants[0] || null;
    };

    const buildSavedLabel = (savedTotal) => {
      const formattedSaved = formatMoney(savedTotal);
      const hasLegacyToken = savedLabelTemplate.includes('{{saved_total}}');
      const hasToken = hasLegacyToken || savedLabelTemplate.includes('[saved_total]');
      const template = hasToken ? savedLabelTemplate : "YOU'RE SAVING [saved_total]";

      return template
        .split('{{saved_total}}').join(formattedSaved)
        .split('[saved_total]').join(formattedSaved);
    };

    const calculateOffer = (bar, variant) => {
      const quantity = Math.max(1, Number(bar.dataset.quantity) || 1);
      const discountType = (bar.dataset.discountType || 'none').toLowerCase();
      const discountValue = Number(bar.dataset.discountValue) || 0;

      const unitPrice = Number(variant && variant.price) || 0;
      const compareUnitPrice = Number(variant && variant.compare_at_price) > unitPrice
        ? Number(variant.compare_at_price)
        : unitPrice;

      const compareTotal = Math.round(compareUnitPrice * quantity);
      let priceTotal = Math.round(unitPrice * quantity);

      if (discountType === 'percentage' && discountValue > 0) {
        priceTotal = Math.round(priceTotal * (1 - (discountValue / 100)));
      } else if (discountType === 'fixed_amount' && discountValue > 0) {
        priceTotal = Math.round(priceTotal - (discountValue * 100));
      }

      if (priceTotal < 0) {
        priceTotal = 0;
      }

      const savedTotal = Math.max(0, compareTotal - priceTotal);

      return {
        quantity,
        priceTotal,
        compareTotal,
        savedTotal
      };
    };

    const updateBarPricing = (bar, variant) => {
      const data = calculateOffer(bar, variant);

      const priceEl = bar.querySelector('[data-bundle-price]');
      const compareEl = bar.querySelector('[data-bundle-compare]');
      const savedEl = bar.querySelector('[data-bundle-saved]');

      if (priceEl) {
        priceEl.textContent = formatMoney(data.priceTotal);
      }

      if (compareEl) {
        if (data.compareTotal > data.priceTotal) {
          compareEl.hidden = false;
          compareEl.textContent = formatMoney(data.compareTotal);
        } else {
          compareEl.hidden = true;
          compareEl.textContent = '';
        }
      }

      if (savedEl) {
        if (data.savedTotal > 0) {
          savedEl.hidden = false;
          savedEl.textContent = buildSavedLabel(data.savedTotal);
        } else {
          savedEl.hidden = true;
          savedEl.textContent = '';
        }
      }
    };

    const updateAllPricing = () => {
      const variant = getCurrentVariant();
      bars.forEach((bar) => {
        updateBarPricing(bar, variant);
      });
    };

    const getSelectedBar = () => {
      return bars.find((bar) => {
        const radio = bar.querySelector('[data-bundle-radio]');
        return radio && radio.checked;
      }) || null;
    };

    const writeBundlePayload = (bar, quantity) => {
      if (!propertyInput) return;

      const payload = {
        id: bar.dataset.offerId || '',
        title: bar.dataset.offerTitle || '',
        quantity
      };

      try {
        propertyInput.value = JSON.stringify(payload);
      } catch (error) {
        propertyInput.value = `${payload.title} x${payload.quantity}`;
      }
    };

    const setSelectedBar = (nextBar) => {
      if (!nextBar) return;

      bars.forEach((bar) => {
        const radio = bar.querySelector('[data-bundle-radio]');
        const isActive = bar === nextBar;

        bar.classList.toggle('is-selected', isActive);
        if (radio) {
          radio.checked = isActive;
        }
      });

      const quantity = Math.max(1, Number(nextBar.dataset.quantity) || 1);
      if (quantityInput) {
        quantityInput.value = String(quantity);
      }

      writeBundlePayload(nextBar, quantity);
    };

    const onRadioChangeCallbacks = [];

    bars.forEach((bar) => {
      const radio = bar.querySelector('[data-bundle-radio]');
      if (!radio) return;

      const onRadioChange = () => {
        setSelectedBar(bar);
        updateAllPricing();
      };

      radio.addEventListener('change', onRadioChange);
      onRadioChangeCallbacks.push(() => {
        radio.removeEventListener('change', onRadioChange);
      });
    });

    const selectedByChecked = getSelectedBar();
    const selectedByDefaultId = defaultOfferId
      ? bars.find((bar) => bar.dataset.offerId === defaultOfferId)
      : null;
    const initialSelectedBar = selectedByChecked || selectedByDefaultId || bars[0];

    setSelectedBar(initialSelectedBar);
    updateAllPricing();

    const onUiUpdated = () => {
      updateAllPricing();
      const currentSelectedBar = getSelectedBar() || initialSelectedBar;
      setSelectedBar(currentSelectedBar);
    };

    const onSubmit = () => {
      const currentSelectedBar = getSelectedBar() || initialSelectedBar;
      setSelectedBar(currentSelectedBar);
    };

    section.addEventListener('hai:product:ui-updated', onUiUpdated);
    formEl.addEventListener('submit', onSubmit);

    addCleanup(state, () => {
      onRadioChangeCallbacks.forEach((callback) => {
        callback();
      });

      section.removeEventListener('hai:product:ui-updated', onUiUpdated);
      formEl.removeEventListener('submit', onSubmit);

      if (quantityInputCreated && quantityInput && quantityInput.parentNode) {
        quantityInput.parentNode.removeChild(quantityInput);
      }

      if (propertyInputCreated && propertyInput && propertyInput.parentNode) {
        propertyInput.parentNode.removeChild(propertyInput);
      }

      delete bundleOffer.dataset.productMainBundleBound;
    });
  }

  function applyHaiAddonPromotion(addonRoot, formEl) {
    if (!addonRoot) return;

    const threshold = toPositiveInteger(addonRoot.dataset.haiAddonThreshold, 1);
    const rate = Math.max(0, Number(addonRoot.dataset.haiAddonRate) || 0);
    const addonItems = Array.from(addonRoot.querySelectorAll('[data-hai-addon-item]'));

    if (!addonItems.length) return;

    const formId = formEl ? formEl.getAttribute('id') : '';
    let quantityInput = formEl ? formEl.querySelector('input[name="quantity"]') : null;
    if (!quantityInput && formId) {
      quantityInput = document.querySelector(`input[name="quantity"][form="${formId}"]`);
    }

    const mainQuantity = toPositiveInteger(quantityInput ? quantityInput.value : 1, 1);

    const selectedItems = addonItems.filter((itemEl) => syncHaiAddonItemSelectionState(itemEl));

    const selectedCount = selectedItems.length;
    const reachedThreshold = selectedCount >= threshold;
    const statusEl = addonRoot.querySelector('[data-hai-addon-status]');
    if (statusEl) {
      const emptyStatus = addonRoot.dataset.haiAddonStatusEmpty || 'Optional';
      const selectedStatus = addonRoot.dataset.haiAddonStatusSelected || '{{count}} selected';
      statusEl.textContent = selectedCount > 0
        ? selectedStatus.replace(/\{\{\s*count\s*\}\}/g, String(selectedCount))
        : emptyStatus;
      statusEl.classList.toggle('has-selection', selectedCount > 0);
    }

    let savingsTotal = 0;

    addonItems.forEach((itemEl) => {
      const salePrice = Math.max(0, Number(itemEl.dataset.haiAddonSalePrice) || 0);
      const regularPrice = Math.max(salePrice, Number(itemEl.dataset.haiAddonRegularPrice) || salePrice);
      const isRequired = itemEl.dataset.haiAddonRequired === 'true';
      const isSelected = selectedItems.includes(itemEl);

      const salePriceEl = itemEl.querySelector('[data-hai-addon-price-sale]');
      const regularPriceEl = itemEl.querySelector('[data-hai-addon-price-regular]');

      if (reachedThreshold) {
        if (salePriceEl) {
          salePriceEl.hidden = false;
          salePriceEl.textContent = formatMoney(salePrice);
        }

        if (regularPriceEl) {
          const shouldShowRegular = regularPrice > salePrice;
          regularPriceEl.hidden = !shouldShowRegular;
          regularPriceEl.textContent = shouldShowRegular ? formatMoney(regularPrice) : '';
        }

        if (isSelected) {
          const lineQty = isRequired ? mainQuantity : 1;
          savingsTotal += Math.max(0, regularPrice - salePrice) * lineQty;
        }
      } else {
        if (salePriceEl) {
          salePriceEl.hidden = false;
          salePriceEl.textContent = formatMoney(regularPrice);
        }

        if (regularPriceEl) {
          regularPriceEl.hidden = true;
          regularPriceEl.textContent = '';
        }
      }
    });

    const headerEl = addonRoot.querySelector('[data-hai-addon-header]');
    if (!headerEl) return;

    // 自定义标题优先；留空时继续使用下方原有的动态促销标题。
    const customHeader = (addonRoot.dataset.haiAddonCustomHeader || '').trim();
    if (customHeader) {
      headerEl.textContent = customHeader.replace(/<br\s*\/?>/gi, '\n');
      return;
    }

    // 主题只能展示附加服务，不能单独改变 Shopify 结账价；零折扣时不得生成促销话术。
    if (rate <= 0) {
      headerEl.textContent = addonRoot.dataset.haiAddonHeaderNoDiscount || 'Optional services';
      return;
    }

    const pendingTemplate = addonRoot.dataset.haiAddonHeaderPending || 'Add {{remaining}} & Save {{rate}}%';
    const reachedTemplate = addonRoot.dataset.haiAddonHeaderReached || 'Order Now & Save {{savings}}';

    if (reachedThreshold) {
      headerEl.textContent = reachedTemplate
        .replace(/\{\{\s*savings\s*\}\}/g, formatMoney(savingsTotal))
        .replace(/\{\{\s*rate\s*\}\}/g, String(rate))
        .replace(/\{\{\s*remaining\s*\}\}/g, '0');
      return;
    }

    const remaining = Math.max(0, threshold - selectedCount);
    headerEl.textContent = pendingTemplate
      .replace(/\{\{\s*remaining\s*\}\}/g, String(remaining))
      .replace(/\{\{\s*rate\s*\}\}/g, String(rate))
      .replace(/\{\{\s*savings\s*\}\}/g, formatMoney(0));
  }

  function isHaiAddonItemSelected(itemEl) {
    if (!itemEl) return false;

    const isVariantAvailable = itemEl.dataset.haiAddonVariantAvailable !== 'false';
    if (!isVariantAvailable) {
      return false;
    }

    if (itemEl.dataset.haiAddonRequired === 'true') {
      return true;
    }

    const checkbox = itemEl.querySelector('[data-hai-addon-checkbox]');
    if (checkbox) {
      return Boolean(checkbox.checked);
    }

    return itemEl.classList.contains('is-selected');
  }

  function syncHaiAddonItemSelectionState(itemEl) {
    if (!itemEl) return false;

    const isSelected = isHaiAddonItemSelected(itemEl);
    itemEl.dataset.haiAddonSelected = isSelected ? 'true' : 'false';
    itemEl.classList.toggle('is-selected', isSelected);

    return isSelected;
  }

  function syncHaiAddonSelectionState(section, formEl) {
    if (!section) return;

    const addonRoots = Array.from(section.querySelectorAll('[data-hai-addon-root]'));
    addonRoots.forEach((addonRoot) => {
      const addonItems = Array.from(addonRoot.querySelectorAll('[data-hai-addon-item]'));
      addonItems.forEach((itemEl) => {
        syncHaiAddonItemSelectionState(itemEl);
      });

      applyHaiAddonPromotion(addonRoot, formEl);
    });
  }

  function syncHaiAddonItemAvailabilityState(itemEl) {
    if (!itemEl) return;

    const isAvailable = itemEl.dataset.haiAddonVariantAvailable !== 'false';
    const isRequired = itemEl.dataset.haiAddonRequired === 'true';
    const checkbox = itemEl.querySelector('[data-hai-addon-checkbox]');
    const variantSelect = itemEl.querySelector('[data-hai-addon-variant-select]');
    const card = itemEl.querySelector('.hai-addon-bundle__card');

    itemEl.classList.toggle('is-unavailable', !isAvailable);
    itemEl.setAttribute('aria-disabled', isAvailable ? 'false' : 'true');

    if (card) {
      card.setAttribute('aria-disabled', isAvailable ? 'false' : 'true');
    }

    if (checkbox) {
      const shouldDisable = !isAvailable || isRequired;
      checkbox.disabled = shouldDisable;
      checkbox.setAttribute('aria-disabled', shouldDisable ? 'true' : 'false');

      if (!isAvailable) {
        checkbox.checked = false;
      } else if (isRequired) {
        checkbox.checked = true;
      }
    }

    if (variantSelect) {
      variantSelect.disabled = !isAvailable;
      variantSelect.setAttribute('aria-disabled', isAvailable ? 'false' : 'true');
    }
  }

  function syncHaiAddonVariantItem(itemEl, addonRoot) {
    if (!itemEl) return;

    const variantSelect = itemEl.querySelector('[data-hai-addon-variant-select]');
    if (variantSelect) {
      const selectedOption = variantSelect.options[variantSelect.selectedIndex];
      if (selectedOption) {
        const variantId = toPositiveInteger(selectedOption.value, 0);
        if (variantId) {
          const regularPrice = Math.max(0, Number(selectedOption.dataset.haiAddonPrice) || 0);
          const rate = Math.max(
            0,
            Number(itemEl.dataset.haiAddonFunctionRate || (addonRoot ? addonRoot.dataset.haiAddonRate : 0)) || 0
          );
          const salePrice = Math.max(0, regularPrice - ((regularPrice * rate) / 100));
          const isAvailable = selectedOption.dataset.haiAddonAvailable !== 'false' && !selectedOption.disabled;

          itemEl.dataset.haiAddonVariantId = String(variantId);
          itemEl.dataset.haiAddonRegularPrice = String(Math.round(regularPrice));
          itemEl.dataset.haiAddonSalePrice = String(Math.round(salePrice));
          itemEl.dataset.haiAddonVariantAvailable = isAvailable ? 'true' : 'false';
        }
      }
    }

    syncHaiAddonItemAvailabilityState(itemEl);
  }
  function hasStaticExtendedLineItems(section) {
    if (!section) return false;
    return Boolean(section.querySelector('[data-hai-addon-root], [data-product-gift-root]'));
  }

  function hasGiftCustomizer(section) {
    if (!section) return false;
    return Boolean(section.querySelector('[data-hai-gift-customizer]'));
  }

  function hasSelectedGiftCustomizer(section) {
    if (!section) return false;

    return Array.from(section.querySelectorAll('[data-hai-gift-customizer]')).some((root) => {
      return Boolean(root.querySelector('[data-gift-option-toggle]:checked'));
    });
  }

  function hasExtendedLineItems(section) {
    return hasStaticExtendedLineItems(section) || hasGiftCustomizer(section);
  }

  function syncDynamicCheckoutVisibility(section) {
    if (!section) return;

    const shouldHideDynamicCheckout = hasStaticExtendedLineItems(section) || hasSelectedGiftCustomizer(section);
    section.classList.toggle('has-gift-checkout-guard', shouldHideDynamicCheckout);
    const paymentButtons = Array.from(section.querySelectorAll('.shopify-payment-button'));

    paymentButtons.forEach((paymentButton) => {
      if (!paymentButton) return;

      if (shouldHideDynamicCheckout) {
        paymentButton.hidden = true;
        paymentButton.style.display = 'none';
        paymentButton.setAttribute('aria-hidden', 'true');
        paymentButton.dataset.productMainDynamicCheckoutHidden = 'true';
        return;
      }

      if (paymentButton.dataset.productMainDynamicCheckoutHidden === 'true') {
        paymentButton.hidden = false;
        paymentButton.style.display = '';
        paymentButton.removeAttribute('aria-hidden');
        delete paymentButton.dataset.productMainDynamicCheckoutHidden;
      }
    });
  }

  function bindDynamicCheckoutGuard(section, state) {
    if (!section || !hasExtendedLineItems(section)) return;
    if (section.dataset.productMainDynamicCheckoutGuardBound === 'true') return;

    section.dataset.productMainDynamicCheckoutGuardBound = 'true';
    syncDynamicCheckoutVisibility(section);

    if (typeof MutationObserver !== 'function') {
      addCleanup(state, () => {
        delete section.dataset.productMainDynamicCheckoutGuardBound;
      });
      return;
    }

    const observer = new MutationObserver(() => {
      syncDynamicCheckoutVisibility(section);
    });

    const onGiftCustomizerChange = (event) => {
      if (!safeClosest(event, '[data-hai-gift-customizer]')) return;
      syncDynamicCheckoutVisibility(section);
    };

    observer.observe(section, {
      childList: true,
      subtree: true
    });

    section.addEventListener('change', onGiftCustomizerChange, true);
    section.addEventListener('hai:gift:change', onGiftCustomizerChange);

    addCleanup(state, () => {
      observer.disconnect();
      section.removeEventListener('change', onGiftCustomizerChange, true);
      section.removeEventListener('hai:gift:change', onGiftCustomizerChange);
      const paymentButtons = Array.from(section.querySelectorAll('.shopify-payment-button'));
      paymentButtons.forEach((paymentButton) => {
        if (!paymentButton || paymentButton.dataset.productMainDynamicCheckoutHidden !== 'true') return;
        paymentButton.hidden = false;
        paymentButton.style.display = '';
        paymentButton.removeAttribute('aria-hidden');
        delete paymentButton.dataset.productMainDynamicCheckoutHidden;
      });
      section.classList.remove('has-gift-checkout-guard');
      delete section.dataset.productMainDynamicCheckoutGuardBound;
    });
  }

  function collectHaiAddonItems(section, formEl, parentContext = {}) {
    if (!section || !formEl) return [];

    const parentVariantId = toPositiveInteger(parentContext.variantId, 0);
    if (!parentVariantId) return [];
    const parentGroupId = String(parentContext.groupId || '').trim();

    const formId = formEl.getAttribute('id');
    let quantityInput = formEl.querySelector('input[name="quantity"]');
    if (!quantityInput && formId) {
      quantityInput = section.querySelector(`input[name="quantity"][form="${formId}"]`);
    }

    const parentQuantity = toPositiveInteger(quantityInput ? quantityInput.value : 1, 1);
    const addonRoots = Array.from(section.querySelectorAll('[data-hai-addon-root]'));
    const addonItemMap = new Map();

    addonRoots.forEach((addonRoot) => {
      const addonKey = (addonRoot.dataset.haiAddonKey || '').trim();
      if (!addonKey) return;

      const addonItems = Array.from(addonRoot.querySelectorAll('[data-hai-addon-item]'));
      addonItems.forEach((itemEl) => {
        const isRequired = itemEl.dataset.haiAddonRequired === 'true';
        const isSelected = syncHaiAddonItemSelectionState(itemEl);
        if (!isSelected) return;

        const variantId = toPositiveInteger(itemEl.dataset.haiAddonVariantId, 0);
        if (!variantId) return;

        const isVariantAvailable = itemEl.dataset.haiAddonVariantAvailable !== 'false';
        if (!isVariantAvailable) return;

        const lineQuantity = isRequired ? parentQuantity : 1;
        const handle = itemEl.dataset.haiAddonProductHandle || '';
        const mapKey = `${variantId}:${addonKey}:${isRequired ? 'required' : 'optional'}`;

        if (addonItemMap.has(mapKey)) {
          const existing = addonItemMap.get(mapKey);
          if (isRequired) {
            existing.quantity = Math.max(existing.quantity, lineQuantity);
          }
          return;
        }

        addonItemMap.set(mapKey, {
          id: variantId,
          quantity: lineQuantity,
          properties: {
            _hai_addon_type: 'cross_sell',
            _hai_addon_key: addonKey,
            _hai_parent_group_id: parentGroupId,
            _hai_addon_parent: String(parentVariantId),
            _hai_addon_required: isRequired ? 'true' : 'false',
            _hai_addon_handle: handle
          }
        });
      });
    });

    return Array.from(addonItemMap.values());
  }

  function collectProductGiftItems(section, formEl, parentContext = {}) {
    if (!section || !formEl) return [];

    const parentVariantId = toPositiveInteger(parentContext.variantId, 0);
    if (!parentVariantId) return [];
    const parentGroupId = String(parentContext.groupId || '').trim();

    const formId = formEl.getAttribute('id');
    let quantityInput = formEl.querySelector('input[name="quantity"]');
    if (!quantityInput && formId) {
      quantityInput = section.querySelector(`input[name="quantity"][form="${formId}"]`);
    }

    const parentQuantity = toPositiveInteger(quantityInput ? quantityInput.value : 1, 1);
    const giftRoots = Array.from(section.querySelectorAll('[data-product-gift-root]'));
    const giftItemMap = new Map();

    giftRoots.forEach((giftRoot) => {
      const campaignKey = (giftRoot.dataset.productGiftCampaign || '').trim();
      const quantityMode = (giftRoot.dataset.productGiftQuantityMode || 'per_order').trim() || 'per_order';
      const badgeText = (giftRoot.dataset.productGiftBadgeText || 'FREE GIFT').trim() || 'FREE GIFT';
      if (!campaignKey) return;

      const giftItems = Array.from(giftRoot.querySelectorAll('[data-product-gift-item]'));
      giftItems.forEach((itemEl) => {
        if (itemEl.dataset.productGiftAvailable !== 'true') return;

        const variantId = toPositiveInteger(itemEl.dataset.productGiftVariantId, 0);
        if (!variantId) return;

        const handle = itemEl.dataset.productGiftProductHandle || '';
        const lineQuantity = quantityMode === 'match_main_quantity' ? parentQuantity : 1;
        const mapKey = `${variantId}:${campaignKey}:${quantityMode}`;

        if (giftItemMap.has(mapKey)) {
          return;
        }

        giftItemMap.set(mapKey, {
          id: variantId,
          quantity: lineQuantity,
          properties: {
            _hai_gift_type: 'gift_item',
            _hai_gift_source: 'product_gift',
            _hai_gift_campaign: campaignKey,
            _hai_parent_group_id: parentGroupId,
            _hai_gift_parent: String(parentVariantId),
            _hai_gift_quantity_mode: quantityMode,
            _hai_gift_handle: handle,
            _hai_gift_badge_text: badgeText
          }
        });
      });
    });

    return Array.from(giftItemMap.values());
  }

  function resolveGiftCustomizerRequestId(root, fallback = '', options = {}) {
    if (!root) return fallback;

    const requestInput = root.querySelector('[data-gift-request-id-input]');
    const forceNew = options && options.forceNew === true;
    const existingId = String(root.dataset.giftRequestId || (requestInput ? requestInput.value : '') || '').trim();
    if (existingId && !forceNew) return existingId;

    const requestId = fallback || generateHaiGroupId();
    root.dataset.giftRequestId = requestId;

    if (requestInput) {
      requestInput.value = requestId;
    }

    return requestId;
  }

  function refreshGiftCustomizerRequestIds(section) {
    if (!section) return;

    const roots = Array.from(section.querySelectorAll('[data-hai-gift-customizer]'));
    roots.forEach((root) => {
      const hasSelectedOption = Array.from(root.querySelectorAll('[data-gift-option-toggle]')).some((toggle) => toggle.checked);
      if (!hasSelectedOption) return;

      resolveGiftCustomizerRequestId(root, generateHaiGroupId(), { forceNew: true });
    });
  }

  function collectGiftCustomizerItems(section, formEl, parentContext = {}) {
    if (!section || !formEl) return [];

    const parentVariantId = toPositiveInteger(parentContext.variantId, 0);
    if (!parentVariantId) return [];

    const parentGroupId = String(parentContext.groupId || '').trim();
    const roots = Array.from(section.querySelectorAll('[data-hai-gift-customizer]'));
    const serviceItemMap = new Map();

    roots.forEach((root) => {
      const videoToggle = root.querySelector('[data-video-message-toggle]');
      if (!videoToggle || !videoToggle.checked) return;

      const variantId = toPositiveInteger(root.dataset.videoGreetingVariantId, 0);
      if (!variantId) return;

      const requestId = resolveGiftCustomizerRequestId(root, parentGroupId);
      const handle = root.dataset.videoGreetingProductHandle || '';
      const title = root.dataset.videoGreetingTitle || 'Personal video message';
      const cartTitle = root.dataset.videoGreetingCartTitle || 'Video message · record after checkout';
      const mapKey = `${variantId}:${requestId || parentGroupId}`;

      if (serviceItemMap.has(mapKey)) return;

      serviceItemMap.set(mapKey, {
        id: variantId,
        quantity: 1,
        properties: {
          _hai_gift_service: 'video_message',
          _hai_gift_request_id: requestId,
          _hai_parent_group_id: parentGroupId,
          _hai_parent_variant_id: String(parentVariantId),
          _hai_service_handle: handle,
          _hai_service_title: title,
          _hai_cart_title: cartTitle,
          _hai_record_after_checkout: 'true'
        }
      });
    });

    return Array.from(serviceItemMap.values());
  }

  function getGiftCustomizerSelectionError(section) {
    if (!section) return '';

    const roots = Array.from(section.querySelectorAll('[data-hai-gift-customizer]'));
    for (let index = 0; index < roots.length; index += 1) {
      const root = roots[index];
      const videoToggle = root.querySelector('[data-video-message-toggle]');
      if (!videoToggle || !videoToggle.checked) continue;

      if (root.dataset.videoGreetingConfigured !== 'true') {
        return root.dataset.videoGreetingConfigError || 'Please select the video message service product before adding to cart.';
      }

      if (root.dataset.videoGreetingAvailable === 'false') {
        return root.dataset.videoGreetingUnavailableError || 'The video message service is unavailable right now.';
      }
    }

    return '';
  }

  function collectExtendedItems(section, formEl, parentContext = {}) {
    return collectHaiAddonItems(section, formEl, parentContext).concat(
      collectProductGiftItems(section, formEl, parentContext),
      collectGiftCustomizerItems(section, formEl, parentContext)
    );
  }

  function collectProductFormProperties(section, formEl) {
    if (!section || !formEl) return {};

    const formData = new FormData(formEl);
    const mainProperties = {};

    formData.forEach((value, key) => {
      const match = typeof key === 'string' ? key.match(/^properties\[(.+)\]$/) : null;
      if (!match) return;

      const propertyKey = match[1];
      const propertyValue = typeof value === 'string' ? value : String(value);

      if (propertyValue === '') return;
      mainProperties[propertyKey] = propertyValue;
    });

    const formId = formEl.getAttribute('id');
    if (!formId) {
      return mainProperties;
    }

    const externalPropertyInputs = Array.from(
      section.querySelectorAll(`[name^="properties["][form="${formId}"]`)
    );

    externalPropertyInputs.forEach((inputEl) => {
      if (inputEl.disabled) return;

      const inputType = String(inputEl.type || '').toLowerCase();
      if ((inputType === 'checkbox' || inputType === 'radio') && !inputEl.checked) return;

      const match = inputEl.name ? inputEl.name.match(/^properties\[(.+)\]$/) : null;
      if (!match) return;

      const propertyKey = match[1];
      const propertyValue = inputEl.value == null ? '' : String(inputEl.value);
      if (propertyValue === '') return;

      mainProperties[propertyKey] = propertyValue;
    });

    return mainProperties;
  }

  function buildMainProductItem(section, formEl, options = {}) {
    if (!section || !formEl) return null;

    const resolvedVariantId = toPositiveInteger(options.variantId, 0) || resolveAssociatedVariantId(section, formEl);
    if (!resolvedVariantId) return null;

    const formId = formEl.getAttribute('id');
    let quantityInput = formEl.querySelector('input[name="quantity"]');
    if (!quantityInput && formId) {
      quantityInput = section.querySelector(`input[name="quantity"][form="${formId}"]`);
    }

    const mainQuantity = toPositiveInteger(quantityInput ? quantityInput.value : 1, 1);
    const mainProperties = collectProductFormProperties(section, formEl);
    const groupId = String(options.groupId || '').trim();
    const addonItems = Array.isArray(options.addonItems) ? options.addonItems : [];

    if (groupId) {
      mainProperties._hai_group_id = groupId;
    }

    if (addonItems.length) {
      const sharedAddonKey = addonItems[0] && addonItems[0].properties
        ? addonItems[0].properties._hai_addon_key
        : '';

      if (sharedAddonKey) {
        mainProperties._hai_addon_key = sharedAddonKey;
      }
      mainProperties._hai_addon_type = 'main_product';
    }

    const mainItem = {
      id: resolvedVariantId,
      quantity: mainQuantity,
      properties: mainProperties
    };

    const sellingPlanInput = formEl.querySelector('[name="selling_plan"]');
    if (sellingPlanInput && sellingPlanInput.value) {
      const parsedSellingPlan = Number(sellingPlanInput.value);
      mainItem.selling_plan = Number.isFinite(parsedSellingPlan) && parsedSellingPlan > 0
        ? parsedSellingPlan
        : sellingPlanInput.value;
    }

    return mainItem;
  }

  function buildExtendedProductPayload(section, formEl) {
    if (!section || !formEl) return null;

    const mainVariantId = resolveAssociatedVariantId(section, formEl);
    if (!mainVariantId) return null;
    const groupId = generateHaiGroupId();
    const parentContext = {
      variantId: mainVariantId,
      groupId
    };
    refreshGiftCustomizerRequestIds(section);

    const addonItems = collectHaiAddonItems(section, formEl, parentContext);
    const giftItems = collectProductGiftItems(section, formEl, parentContext);
    const giftCustomizerItems = collectGiftCustomizerItems(section, formEl, parentContext);
    const extraItems = addonItems.concat(giftItems, giftCustomizerItems);
    if (!extraItems.length) return null;

    const mainItem = buildMainProductItem(section, formEl, {
      variantId: mainVariantId,
      groupId,
      addonItems
    });
    if (!mainItem) return null;

    return {
      items: [mainItem].concat(extraItems)
    };
  }

  function buildProductSubmitPayload(section, formEl) {
    if (!section || !formEl) return null;

    const extendedPayload = buildExtendedProductPayload(section, formEl);
    if (extendedPayload && Array.isArray(extendedPayload.items) && extendedPayload.items.length) {
      return extendedPayload;
    }

    const mainItem = buildMainProductItem(section, formEl);
    if (!mainItem) return null;

    return {
      items: [mainItem]
    };
  }

  function payloadRequiresAtomicAdd(payload) {
    if (!payload || !Array.isArray(payload.items)) return false;

    return payload.items.some((item) => {
      return item
        && item.properties
        && item.properties._hai_gift_service === 'video_message';
    });
  }

  function getComparableLineItemProperties(properties) {
    if (!properties || typeof properties !== 'object') return {};

    const comparable = {};

    Object.keys(properties).forEach((key) => {
      const rawValue = properties[key];
      if (rawValue === null || rawValue === undefined) return;

      const normalizedValue = String(rawValue).trim();
      if (!normalizedValue) return;

      comparable[key] = normalizedValue;
    });

    return comparable;
  }

  function getMatchingCartItemQuantity(cart, item) {
    if (!cart || !Array.isArray(cart.items) || !item || !item.id) return 0;

    const expectedId = Number(item.id);
    if (!expectedId) return 0;

    const expectedProperties = getComparableLineItemProperties(item.properties);
    const expectedKeys = Object.keys(expectedProperties);
    if (!expectedKeys.length) return 0;

    return cart.items.reduce((total, line) => {
      if (!line || Number(line.id) !== expectedId) {
        return total;
      }

      const lineProperties = getComparableLineItemProperties(line.properties);
      const isMatch = expectedKeys.every((key) => lineProperties[key] === expectedProperties[key]);
      if (!isMatch) {
        return total;
      }

      return total + toPositiveInteger(line.quantity, 0);
    }, 0);
  }

  function buildCartAddExtra(item) {
    const extra = {};

    if (item && item.selling_plan) {
      extra.selling_plan = item.selling_plan;
    }

    return extra;
  }

  async function reconcileExtendedExtraItems(extraItems, cart) {
    if (!Array.isArray(extraItems) || !extraItems.length) {
      return { cart, failedCount: 0 };
    }

    if (
      !window.Hai
      || !window.Hai.cart
      || typeof window.Hai.cart.addItem !== 'function'
      || typeof window.Hai.cart.getCart !== 'function'
    ) {
      return { cart, failedCount: 0 };
    }

    const missingItems = extraItems.reduce((result, item) => {
      if (!item || !item.id) return result;

      const expectedQuantity = toPositiveInteger(item.quantity, 1);
      const currentQuantity = getMatchingCartItemQuantity(cart, item);
      const missingQuantity = Math.max(0, expectedQuantity - currentQuantity);

      if (missingQuantity > 0) {
        result.push({
          ...item,
          quantity: missingQuantity
        });
      }

      return result;
    }, []);

    if (!missingItems.length) {
      return { cart, failedCount: 0 };
    }

    let failedCount = 0;

    for (let index = 0; index < missingItems.length; index += 1) {
      const item = missingItems[index];

      try {
        await window.Hai.cart.addItem(
          item.id,
          item.quantity || 1,
          item.properties || {},
          buildCartAddExtra(item)
        );
      } catch (error) {
        failedCount += 1;
        console.warn('Product extended retry add failed:', error);
      }
    }

    const nextCart = await window.Hai.cart.getCart();
    return {
      cart: nextCart,
      failedCount
    };
  }

  function syncBundleOfferSubmissionState(section, formEl) {
    if (!section || !formEl) return;

    const selectedBundleRadio = section.querySelector('[data-bundle-bar] [data-bundle-radio]:checked');
    const selectedBundleBar = selectedBundleRadio
      ? selectedBundleRadio.closest('[data-bundle-bar]')
      : section.querySelector('[data-bundle-bar]');
    if (!selectedBundleBar) return;

    const quantity = toPositiveInteger(selectedBundleBar.dataset.quantity, 1);
    const formId = formEl.getAttribute('id');

    let quantityInput = formEl.querySelector('input[name="quantity"]');
    if (!quantityInput && formId) {
      quantityInput = section.querySelector(`input[name="quantity"][form="${formId}"]`);
    }
    if (quantityInput) {
      quantityInput.value = String(quantity);
    }

    const propertyInput = section.querySelector('[data-bundle-property-input]')
      || formEl.querySelector('input[name="properties[_bundle_offer]"]');
    if (!propertyInput) return;

    const payload = {
      id: selectedBundleBar.dataset.offerId || '',
      title: selectedBundleBar.dataset.offerTitle || '',
      quantity
    };

    try {
      propertyInput.value = JSON.stringify(payload);
    } catch (error) {
      propertyInput.value = `${payload.title} x${payload.quantity}`;
    }
  }

  function bindProductSubmit(section, state) {
    const formEl = section.querySelector('[data-product-form] form') || section.querySelector('form[action*="/cart/add"]');
    if (!formEl || formEl.dataset.productMainExtendedSubmitBound === 'true') return;

    formEl.dataset.productMainExtendedSubmitBound = 'true';
    const formId = formEl.getAttribute('id') || '';
    const buttonStateMap = new WeakMap();

    const submitButtons = Array.from(
      section.querySelectorAll('[data-add-to-cart], [data-sticky-add], [data-sales-add-to-cart], button[type="submit"][name="add"], input[type="submit"][name="add"]')
    ).filter((buttonEl) => {
      if (buttonEl.matches('[data-sticky-add], [data-sales-add-to-cart]')) {
        return true;
      }

      const targetForm = buttonEl.getAttribute('form');
      if (targetForm) {
        return formId ? targetForm === formId : Boolean(buttonEl.form === formEl);
      }
      return buttonEl.form === formEl || formEl.contains(buttonEl);
    });

    const toggleSubmittingState = (isSubmitting) => {
      submitButtons.forEach((buttonEl) => {
        if (!buttonEl) return;

        if (isSubmitting) {
          if (!buttonStateMap.has(buttonEl)) {
            buttonStateMap.set(buttonEl, {
              disabled: Boolean(buttonEl.disabled),
              ariaBusy: buttonEl.getAttribute('aria-busy')
            });
          }
          buttonEl.disabled = true;
          buttonEl.setAttribute('aria-busy', 'true');
          return;
        }

        const previousState = buttonStateMap.get(buttonEl);
        if (previousState) {
          buttonEl.disabled = previousState.disabled;
          if (previousState.ariaBusy == null) {
            buttonEl.removeAttribute('aria-busy');
          } else {
            buttonEl.setAttribute('aria-busy', previousState.ariaBusy);
          }
          buttonStateMap.delete(buttonEl);
          return;
        }

        buttonEl.disabled = false;
        buttonEl.removeAttribute('aria-busy');
      });
    };

    const canSubmitProduct = () => {
      syncBundleOfferSubmissionState(section, formEl);
      syncHaiAddonSelectionState(section, formEl);

      const giftCustomizerError = getGiftCustomizerSelectionError(section);
      if (giftCustomizerError) {
        return { error: giftCustomizerError };
      }

      if (!window.Hai || !window.Hai.cart) {
        return null;
      }

      const hasBulkAdd = typeof window.Hai.cart.addItems === 'function';
      const hasSingleAdd = typeof window.Hai.cart.addItem === 'function';
      if (!hasBulkAdd && !hasSingleAdd) {
        return null;
      }

      const payload = buildProductSubmitPayload(section, formEl);
      if (!payload || !Array.isArray(payload.items) || !payload.items.length) {
        return null;
      }

      if (payloadRequiresAtomicAdd(payload) && !hasBulkAdd) {
        return { error: 'Unable to add video message right now. Please try again.' };
      }

      return payload;
    };

    const submitProductPayload = async (payload) => {
      if (!payload || !Array.isArray(payload.items) || !payload.items.length) {
        return;
      }

      formEl.dataset.productMainExtendedSubmitting = 'true';
      toggleSubmittingState(true);
      let usedFallback = false;
      let failedSupplementalCount = 0;

      try {
        const hasBulkAdd = window.Hai
          && window.Hai.cart
          && typeof window.Hai.cart.addItems === 'function';
        const hasSingleAdd = window.Hai
          && window.Hai.cart
          && typeof window.Hai.cart.addItem === 'function';

        if (!hasBulkAdd && !hasSingleAdd) {
          throw new Error('Cart add methods are unavailable');
        }

        try {
          if (!hasBulkAdd) {
            throw new Error('Bulk add is unavailable');
          }

          await window.Hai.cart.addItems(payload.items);
        } catch (bulkError) {
          if (payloadRequiresAtomicAdd(payload)) {
            throw bulkError;
          }

          console.warn('Product extended bulk add failed, fallback to incremental add:', bulkError);

          if (!hasSingleAdd) {
            throw bulkError;
          }

          const mainItem = payload.items[0];
          const extraItems = payload.items.slice(1);
          if (!mainItem || !mainItem.id) {
            throw bulkError;
          }

          await window.Hai.cart.addItem(
            mainItem.id,
            mainItem.quantity || 1,
            mainItem.properties || {},
            buildCartAddExtra(mainItem)
          );

          usedFallback = true;
          for (let index = 0; index < extraItems.length; index += 1) {
            const extraItem = extraItems[index];
            if (!extraItem || !extraItem.id) continue;

            try {
              await window.Hai.cart.addItem(
                extraItem.id,
                extraItem.quantity || 1,
                extraItem.properties || {},
                buildCartAddExtra(extraItem)
              );
            } catch (extraItemError) {
              failedSupplementalCount += 1;
              console.warn('Product extended incremental add failed:', extraItemError);
            }
          }
        }

        let cart = null;
        if (typeof window.Hai.cart.getCart === 'function') {
          cart = await window.Hai.cart.getCart();
        }

        if (cart) {
          const reconciliation = await reconcileExtendedExtraItems(payload.items.slice(1), cart);
          cart = reconciliation.cart;
          failedSupplementalCount += reconciliation.failedCount;
        }

        if (cart && window.Hai.addonCartSync && typeof window.Hai.addonCartSync.enforce === 'function') {
          cart = await window.Hai.addonCartSync.enforce(cart, { notify: true });
        }
        if (cart && window.Hai.cartNote && typeof window.Hai.cartNote.syncGiftSummaryToCart === 'function') {
          cart = await window.Hai.cartNote.syncGiftSummaryToCart(cart);
        }

        if (cart && window.Hai.cartDrawer && typeof window.Hai.cartDrawer.applyCartState === 'function') {
          await window.Hai.cartDrawer.applyCartState(cart);
          if (typeof window.Hai.cartDrawer.open === 'function') {
            window.Hai.cartDrawer.open();
          }
        }

        if (window.Hai.cartNotification && typeof window.Hai.cartNotification.show === 'function') {
          let message = (window.Hai.strings && (window.Hai.strings.addedToCart || window.Hai.strings.addToCart))
            || 'Added to cart';

          if (failedSupplementalCount > 0) {
            message = `Added to cart (${failedSupplementalCount} extra item unavailable)`;
          }

          window.Hai.cartNotification.show(message);
        }
      } catch (error) {
        console.error('Product extended submit error:', error);
        if (window.Hai.cartNotification && typeof window.Hai.cartNotification.show === 'function') {
          window.Hai.cartNotification.show(
            (window.Hai.strings && window.Hai.strings.cartError) || 'Unable to add to cart'
          );
        }
      } finally {
        toggleSubmittingState(false);
        delete formEl.dataset.productMainExtendedSubmitting;
      }
    };

    const onSubmit = async (event) => {
      if (formEl.dataset.productMainExtendedSubmitting === 'true') {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }

      const payload = canSubmitProduct();
      if (!payload) {
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();
      if (payload.error) {
        if (window.Hai && window.Hai.cartNotification && typeof window.Hai.cartNotification.show === 'function') {
          window.Hai.cartNotification.show(payload.error);
        }
        return;
      }

      await submitProductPayload(payload);
    };

    formEl.addEventListener('submit', onSubmit, true);

    addCleanup(state, () => {
      formEl.removeEventListener('submit', onSubmit, true);
      delete formEl.dataset.productMainExtendedSubmitBound;
      delete formEl.dataset.productMainExtendedSubmitting;
    });
  }

  function initHaiAddonSection(section, state) {
    const addonRoots = Array.from(section.querySelectorAll('[data-hai-addon-root]'));
    if (!addonRoots.length) return;

    const formEl = section.querySelector('[data-product-form] form') || section.querySelector('form[action*="/cart/add"]');
    if (!formEl) return;

    bindProductSubmit(section, state);
    bindDynamicCheckoutGuard(section, state);

    addonRoots.forEach((addonRoot) => {
      if (addonRoot.dataset.productMainHaiAddonBound === 'true') return;
      addonRoot.dataset.productMainHaiAddonBound = 'true';

      const addonItems = Array.from(addonRoot.querySelectorAll('[data-hai-addon-item]'));
      const moreButton = addonRoot.querySelector('[data-hai-addon-more]');
      const isHorizontal = addonRoot.dataset.haiAddonLayout === 'horizontal';
      const sliderEl = addonRoot.querySelector('[data-hai-addon-slider]');
      const previousButton = addonRoot.querySelector('[data-hai-addon-prev]');
      const nextButton = addonRoot.querySelector('[data-hai-addon-next]');
      const limit = toPositiveInteger(addonRoot.dataset.haiAddonLimit, addonItems.length || 1);
      const moreButtonText = addonRoot.dataset.haiAddonMoreText || 'View more';
      const lessButtonText = addonRoot.dataset.haiAddonLessText || 'View less';
      let isExpanded = false;
      let addonSwiper = null;
      let swiperRetryTimer = null;

      const renderCollapsedState = () => {
        if (isHorizontal) {
          addonItems.forEach((itemEl) => {
            itemEl.hidden = false;
          });
          if (moreButton) moreButton.hidden = true;
          return;
        }

        addonItems.forEach((itemEl, index) => {
          itemEl.hidden = !isExpanded && index >= limit;
        });

        if (moreButton) {
          const shouldShowToggle = addonItems.length > limit;
          moreButton.hidden = !shouldShowToggle;
          if (shouldShowToggle) {
            moreButton.textContent = isExpanded ? lessButtonText : moreButtonText;
          }
        }
      };

      const updateAddonSwiper = () => {
        if (!addonSwiper || typeof addonSwiper.update !== 'function') return;
        window.requestAnimationFrame(() => addonSwiper.update());
      };

      const initAddonSwiper = (attempt = 0) => {
        if (!isHorizontal || !sliderEl || addonSwiper) return;

        if (typeof window.Swiper !== 'function') {
          if (attempt < 20) {
            swiperRetryTimer = window.setTimeout(() => initAddonSwiper(attempt + 1), 120);
          }
          return;
        }

        addonSwiper = new window.Swiper(sliderEl, {
          slidesPerView: 'auto',
          spaceBetween: 12,
          watchOverflow: true,
          observer: true,
          observeParents: true,
          keyboard: {
            enabled: true,
            onlyInViewport: true,
          },
          navigation: {
            nextEl: nextButton,
            prevEl: previousButton,
          },
        });
      };

      const onAddonChange = (event) => {
        const variantSelect = safeClosest(event, '[data-hai-addon-variant-select]');
        if (variantSelect) {
          const addonItem = variantSelect.closest('[data-hai-addon-item]');
          syncHaiAddonVariantItem(addonItem, addonRoot);
          applyHaiAddonPromotion(addonRoot, formEl);
          updateAddonSwiper();
          return;
        }

        const checkbox = safeClosest(event, '[data-hai-addon-checkbox]');
        if (!checkbox) return;

        const addonItem = checkbox.closest('[data-hai-addon-item]');
        if (addonItem && addonItem.dataset.haiAddonRequired === 'true') {
          checkbox.checked = true;
        }

        applyHaiAddonPromotion(addonRoot, formEl);
        updateAddonSwiper();
      };

      const onMoreClick = () => {
        isExpanded = !isExpanded;
        renderCollapsedState();
      };

      const onUiUpdated = () => {
        applyHaiAddonPromotion(addonRoot, formEl);
        updateAddonSwiper();
      };

      const onSectionChange = (event) => {
        if (safeClosest(event, '[data-bundle-radio]') || safeClosest(event, 'input[name="quantity"]')) {
          applyHaiAddonPromotion(addonRoot, formEl);
        }
      };

      addonItems.forEach((itemEl) => {
        syncHaiAddonVariantItem(itemEl, addonRoot);

        if (itemEl.dataset.haiAddonRequired !== 'true') return;
        const checkbox = itemEl.querySelector('[data-hai-addon-checkbox]');
        if (!checkbox) return;

        if (itemEl.dataset.haiAddonVariantAvailable === 'false') {
          checkbox.checked = false;
          return;
        }

        checkbox.checked = true;
      });

      addonRoot.addEventListener('change', onAddonChange);
      section.addEventListener('hai:product:ui-updated', onUiUpdated);
      section.addEventListener('change', onSectionChange);

      if (moreButton) {
        moreButton.addEventListener('click', onMoreClick);
      }

      renderCollapsedState();
      applyHaiAddonPromotion(addonRoot, formEl);
      initAddonSwiper();

      addCleanup(state, () => {
        addonRoot.removeEventListener('change', onAddonChange);
        section.removeEventListener('hai:product:ui-updated', onUiUpdated);
        section.removeEventListener('change', onSectionChange);

        if (moreButton) {
          moreButton.removeEventListener('click', onMoreClick);
        }

        if (swiperRetryTimer) {
          window.clearTimeout(swiperRetryTimer);
        }

        if (addonSwiper && typeof addonSwiper.destroy === 'function') {
          addonSwiper.destroy(true, true);
        }

        delete addonRoot.dataset.productMainHaiAddonBound;
      });
    });
  }

  function initProductGiftSection(section, state) {
    const giftRoots = Array.from(section.querySelectorAll('[data-product-gift-root]'));
    if (!giftRoots.length) return;

    const formEl = section.querySelector('[data-product-form] form') || section.querySelector('form[action*="/cart/add"]');
    if (!formEl) return;

    bindProductSubmit(section, state);
    bindDynamicCheckoutGuard(section, state);

    giftRoots.forEach((giftRoot) => {
      if (!giftRoot || giftRoot.dataset.productMainGiftBound === 'true') return;
      giftRoot.dataset.productMainGiftBound = 'true';

      addCleanup(state, () => {
        delete giftRoot.dataset.productMainGiftBound;
      });
    });
  }

  function initGiftCustomizerSection(section, state) {
    const customizerRoots = Array.from(section.querySelectorAll('[data-hai-gift-customizer]'));
    if (!customizerRoots.length) return;

    const formEl = section.querySelector('[data-product-form] form') || section.querySelector('form[action*="/cart/add"]');
    if (!formEl) return;

    bindProductSubmit(section, state);
    bindDynamicCheckoutGuard(section, state);

    customizerRoots.forEach((customizerRoot) => {
      if (!customizerRoot || customizerRoot.dataset.productMainGiftCustomizerBound === 'true') return;
      customizerRoot.dataset.productMainGiftCustomizerBound = 'true';

      const onCustomizerChange = () => {
        syncDynamicCheckoutVisibility(section);
      };

      customizerRoot.addEventListener('change', onCustomizerChange);
      customizerRoot.addEventListener('hai:gift:change', onCustomizerChange);

      addCleanup(state, () => {
        customizerRoot.removeEventListener('change', onCustomizerChange);
        customizerRoot.removeEventListener('hai:gift:change', onCustomizerChange);
        delete customizerRoot.dataset.productMainGiftCustomizerBound;
      });
    });
  }

  function initStickySection(section, state) {
    const stickyBar = section.querySelector('[data-sticky-bar]');
    if (!stickyBar) return;
    const pageFooter = document.querySelector('footer');

    const form = section.querySelector('form[action*="/cart/add"]');
    const mainAddButton = section.querySelector('[data-add-to-cart]');
    const mainPriceCurrent = section.querySelector('[data-product-price-current]');
    const mainPriceCompare = section.querySelector('[data-product-price-compare]');

    const stickyAddButton = section.querySelector('[data-sticky-add]');
    const stickyPriceCurrent = section.querySelector('[data-sticky-price-current]');
    const stickyPriceCompare = section.querySelector('[data-sticky-price-compare]');

    const addLabel = section.dataset.addToBagLabel
      || (stickyAddButton ? (stickyAddButton.textContent || '').trim() : '')
      || ((window.Hai && window.Hai.strings && window.Hai.strings.addToCart) || 'Add to bag');
    const soldOutLabel = (window.Hai && window.Hai.strings && window.Hai.strings.soldOut) || 'Sold out';
    const selectVariantLabel = (window.Hai && window.Hai.strings && window.Hai.strings.selectVariant) || 'Select a variant';

    const syncStickyPrice = () => {
      if (!stickyPriceCurrent || !mainPriceCurrent) return;

      const currentText = (mainPriceCurrent.textContent || '').trim();
      const compareText = mainPriceCompare ? (mainPriceCompare.textContent || '').trim() : '';
      const compareHidden = !mainPriceCompare || mainPriceCompare.hidden || compareText === '';

      if (currentText) {
        stickyPriceCurrent.textContent = currentText;
      }

      if (!stickyPriceCompare) return;

      if (compareHidden) {
        stickyPriceCompare.hidden = true;
        stickyPriceCompare.textContent = '';
      } else {
        stickyPriceCompare.hidden = false;
        stickyPriceCompare.textContent = compareText;
      }
    };

    const syncButtonState = () => {
      if (!stickyAddButton) return;

      if (!mainAddButton) {
        stickyAddButton.disabled = true;
        stickyAddButton.textContent = soldOutLabel;
        return;
      }

      const mainButtonText = (mainAddButton.textContent || '').trim();
      const isDisabled =
        mainAddButton.disabled ||
        mainAddButton.getAttribute('aria-disabled') === 'true' ||
        mainButtonText === soldOutLabel ||
        mainButtonText === selectVariantLabel;
      const shouldMirrorMainText = mainButtonText && mainButtonText !== addLabel;

      stickyAddButton.disabled = isDisabled;
      if (isDisabled) {
        stickyAddButton.textContent = mainButtonText || soldOutLabel;
      } else if (shouldMirrorMainText) {
        stickyAddButton.textContent = mainButtonText;
      } else {
        stickyAddButton.textContent = addLabel;
      }
    };

    let delayedSyncTimer = null;
    const syncAfterVariantUiUpdate = () => {
      window.requestAnimationFrame(() => {
        syncStickyPrice();
        syncButtonState();

        if (delayedSyncTimer) {
          window.clearTimeout(delayedSyncTimer);
        }

        delayedSyncTimer = window.setTimeout(() => {
          syncStickyPrice();
          syncButtonState();
        }, 120);
      });
    };

    const onSectionChange = () => {
      syncAfterVariantUiUpdate();
    };

    const onSectionClick = (event) => {
      if (
        safeClosest(event, '[data-variant-option]') ||
        safeClosest(event, '[data-gallery-thumb]') ||
        safeClosest(event, '[data-add-to-cart]')
      ) {
        syncAfterVariantUiUpdate();
      }
    };

    const onUiUpdated = () => {
      syncAfterVariantUiUpdate();
    };

    const onStickyAddClick = (event) => {
      event.preventDefault();

      if (
        !mainAddButton ||
        mainAddButton.disabled ||
        mainAddButton.getAttribute('aria-disabled') === 'true'
      ) {
        return;
      }

      if (form && typeof form.requestSubmit === 'function') {
        form.requestSubmit();
        return;
      }

      if (form) {
        form.submit();
        return;
      }

      mainAddButton.click();
    };

    let ticking = false;
    const updateStickyVisibility = () => {
      const sectionTop = section.getBoundingClientRect().top + window.scrollY;
      const trigger = sectionTop + (window.innerHeight * 3.5);
      const stickyHeight = stickyBar.offsetHeight || 0;
      const footerRect = pageFooter ? pageFooter.getBoundingClientRect() : null;
      const footerOverlapsSticky = footerRect
        ? footerRect.top < (window.innerHeight - stickyHeight)
        : false;

      if (window.scrollY > trigger && !footerOverlapsSticky) {
        stickyBar.classList.add('is-visible');
      } else {
        stickyBar.classList.remove('is-visible');
      }
    };

    const onScrollOrResize = () => {
      if (ticking) return;

      ticking = true;
      window.requestAnimationFrame(() => {
        updateStickyVisibility();
        ticking = false;
      });
    };

    syncStickyPrice();
    syncButtonState();
    updateStickyVisibility();

    section.addEventListener('change', onSectionChange);
    section.addEventListener('click', onSectionClick);
    section.addEventListener('hai:product:ui-updated', onUiUpdated);

    if (stickyAddButton) {
      stickyAddButton.addEventListener('click', onStickyAddClick);
    }

    window.addEventListener('scroll', onScrollOrResize, { passive: true });
    window.addEventListener('resize', onScrollOrResize);

    addCleanup(state, () => {
      section.removeEventListener('change', onSectionChange);
      section.removeEventListener('click', onSectionClick);
      section.removeEventListener('hai:product:ui-updated', onUiUpdated);

      if (stickyAddButton) {
        stickyAddButton.removeEventListener('click', onStickyAddClick);
      }

      window.removeEventListener('scroll', onScrollOrResize);
      window.removeEventListener('resize', onScrollOrResize);

      if (delayedSyncTimer) {
        window.clearTimeout(delayedSyncTimer);
      }
    });
  }

  function initPromoSection(section, state) {
    const promoWraps = section.querySelectorAll('[data-promotional-message]');
    if (!promoWraps.length) return;

    const bodyLockClass = 'hai-promo-overflow-hidden';

    const fallbackCopy = (text) => {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.setAttribute('readonly', 'readonly');
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';

      document.body.appendChild(textarea);
      textarea.select();

      let success = false;
      try {
        success = document.execCommand('copy');
      } catch (error) {
        success = false;
      }

      document.body.removeChild(textarea);
      return success;
    };

    promoWraps.forEach((promoWrap) => {
      if (promoWrap.dataset.productMainPromoBound === 'true') return;
      promoWrap.dataset.productMainPromoBound = 'true';

      const tipButton = promoWrap.querySelector('[data-promotional-tip]');
      const popup = promoWrap.querySelector('[data-promotional-popup]');
      const closeButton = promoWrap.querySelector('[data-promotional-close]');
      const codeButton = promoWrap.querySelector('[data-promotional-code]');
      let copiedTimer = null;
      let popupAnchor = null;

      if (popup) {
        const popupOriginalParent = popup.parentNode;
        if (popupOriginalParent && popupOriginalParent !== document.body) {
          popupAnchor = document.createComment('hai-promo-popup-anchor');
          popupOriginalParent.insertBefore(popupAnchor, popup);
          document.body.appendChild(popup);
        }
      }
      const openPopup = () => {
        if (!popup) return;

        popup.hidden = false;
        window.requestAnimationFrame(() => {
          popup.classList.add('is-active');
        });
        document.body.classList.add(bodyLockClass);
      };

      const closePopup = (immediate) => {
        if (!popup) return;

        popup.classList.remove('is-active');
        document.body.classList.remove(bodyLockClass);

        if (immediate) {
          popup.hidden = true;
          return;
        }

        window.setTimeout(() => {
          if (!popup.classList.contains('is-active')) {
            popup.hidden = true;
          }
        }, 180);
      };

      const markCopied = () => {
        if (!codeButton) return;

        if (copiedTimer) {
          window.clearTimeout(copiedTimer);
        }

        codeButton.classList.add('is-copied');
        copiedTimer = window.setTimeout(() => {
          codeButton.classList.remove('is-copied');
          copiedTimer = null;
        }, 1800);
      };

      const onTipButtonClick = (event) => {
        event.preventDefault();
        openPopup();
      };

      const onCloseButtonClick = (event) => {
        event.preventDefault();
        closePopup(false);
      };

      const onPopupClick = (event) => {
        if (event.target === popup) {
          closePopup(false);
        }
      };

      const onDocumentKeydown = (event) => {
        if (event.key === 'Escape' && popup && !popup.hidden) {
          closePopup(false);
        }
      };

      const onCodeButtonClick = (event) => {
        event.preventDefault();

        const code = (codeButton.dataset.code || codeButton.textContent || '').trim();
        if (!code) return;

        if (navigator.clipboard && window.isSecureContext) {
          navigator.clipboard.writeText(code)
            .then(() => {
              markCopied();
            })
            .catch(() => {
              if (fallbackCopy(code)) {
                markCopied();
              }
            });
          return;
        }

        if (fallbackCopy(code)) {
          markCopied();
        }
      };

      if (tipButton) {
        tipButton.addEventListener('click', onTipButtonClick);
      }

      if (closeButton) {
        closeButton.addEventListener('click', onCloseButtonClick);
      }

      if (popup) {
        popup.addEventListener('click', onPopupClick);
      }

      document.addEventListener('keydown', onDocumentKeydown);

      if (codeButton) {
        codeButton.addEventListener('click', onCodeButtonClick);
      }

      addCleanup(state, () => {
        if (tipButton) {
          tipButton.removeEventListener('click', onTipButtonClick);
        }

        if (closeButton) {
          closeButton.removeEventListener('click', onCloseButtonClick);
        }

        if (popup) {
          popup.removeEventListener('click', onPopupClick);
        }

        document.removeEventListener('keydown', onDocumentKeydown);

        if (codeButton) {
          codeButton.removeEventListener('click', onCodeButtonClick);
        }

        closePopup(true);

        if (popupAnchor && popupAnchor.parentNode && popup) {
          popupAnchor.parentNode.insertBefore(popup, popupAnchor);
          popupAnchor.parentNode.removeChild(popupAnchor);
        }

        delete promoWrap.dataset.productMainPromoBound;
      });
    });
  }
  function initFaqBlocksSection(section, state) {
    const faqRoots = section.querySelectorAll('[data-hai-faq-root]');
    if (!faqRoots.length) return;

    const bodyLockClass = 'hai-faq-overflow-hidden';
    const modalSelector = '[data-hai-faq-tip-modal]';

    const syncBodyLock = () => {
      const hasOpenModal = Boolean(document.querySelector(`${modalSelector}:not([hidden])`));
      document.body.classList.toggle(bodyLockClass, hasOpenModal);
    };

    faqRoots.forEach((faqRoot) => {
      if (!faqRoot || faqRoot.dataset.productMainFaqBound === 'true') return;
      faqRoot.dataset.productMainFaqBound = 'true';

      const tipTriggers = Array.from(faqRoot.querySelectorAll('[data-hai-faq-tip-open]'));
      const modals = Array.from(faqRoot.querySelectorAll(modalSelector));
      const modalMap = new Map();
      const modalAnchors = [];
      const bindings = [];

      modals.forEach((modal) => {
        const modalId = modal.getAttribute('data-hai-faq-tip-modal');
        if (modalId) {
          modalMap.set(modalId, modal);
        }

        const modalOriginalParent = modal.parentNode;
        if (modalOriginalParent && modalOriginalParent !== document.body) {
          const modalAnchor = document.createComment('hai-faq-tip-modal-anchor');
          modalOriginalParent.insertBefore(modalAnchor, modal);
          document.body.appendChild(modal);
          modalAnchors.push({ modal, modalAnchor });
        }
      });

      const closeModal = (modal, immediate) => {
        if (!modal) return;

        modal.classList.remove('is-active');

        if (immediate) {
          modal.hidden = true;
          syncBodyLock();
          return;
        }

        window.setTimeout(() => {
          if (!modal.classList.contains('is-active')) {
            modal.hidden = true;
            syncBodyLock();
          }
        }, 180);
      };

      const openModal = (modal, focusTarget) => {
        if (!modal) return;

        modals.forEach((currentModal) => {
          if (currentModal !== modal) {
            closeModal(currentModal, true);
          }
        });

        modal.hidden = false;
        window.requestAnimationFrame(() => {
          modal.classList.add('is-active');
          syncBodyLock();
        });
      };

      tipTriggers.forEach((trigger) => {
        const modalId = trigger.getAttribute('data-hai-faq-tip-open');
        const modal = modalMap.get(modalId);
        if (!modal) return;

        const closeButton = modal.querySelector('[data-hai-faq-tip-close]');

        const onTriggerClick = (event) => {
          event.preventDefault();
          openModal(modal, closeButton);
        };

        const onCloseClick = (event) => {
          event.preventDefault();
          closeModal(modal, false);
        };

        const onModalClick = (event) => {
          if (event.target === modal) {
            closeModal(modal, false);
          }
        };

        trigger.addEventListener('click', onTriggerClick);
        modal.addEventListener('click', onModalClick);

        if (closeButton) {
          closeButton.addEventListener('click', onCloseClick);
        }

        bindings.push({ trigger, modal, closeButton, onTriggerClick, onCloseClick, onModalClick });
      });

      const onDocumentKeydown = (event) => {
        if (event.key !== 'Escape') return;

        modals.forEach((modal) => {
          if (!modal.hidden) {
            closeModal(modal, false);
          }
        });
      };

      document.addEventListener('keydown', onDocumentKeydown);

      addCleanup(state, () => {
        bindings.forEach(({ trigger, modal, closeButton, onTriggerClick, onCloseClick, onModalClick }) => {
          trigger.removeEventListener('click', onTriggerClick);
          modal.removeEventListener('click', onModalClick);

          if (closeButton) {
            closeButton.removeEventListener('click', onCloseClick);
          }
        });

        document.removeEventListener('keydown', onDocumentKeydown);

        modals.forEach((modal) => {
          closeModal(modal, true);
        });

        modalAnchors.forEach(({ modal, modalAnchor }) => {
          if (modalAnchor && modalAnchor.parentNode) {
            modalAnchor.parentNode.insertBefore(modal, modalAnchor);
            modalAnchor.parentNode.removeChild(modalAnchor);
          }
        });

        delete faqRoot.dataset.productMainFaqBound;
      });
    });
  }

  function initSalesCountdownSection(section, state) {
    const countdownBlocks = section.querySelectorAll('[data-sales-countdown]');
    if (!countdownBlocks.length) return;

    const toSafeNumber = (value, fallback) => {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : fallback;
    };

    const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

    countdownBlocks.forEach((countdownEl) => {
      if (!countdownEl || countdownEl.dataset.productMainCountdownBound === 'true') return;
      countdownEl.dataset.productMainCountdownBound = 'true';

      const daysEl = countdownEl.querySelector('[data-countdown-days]');
      const hoursEl = countdownEl.querySelector('[data-countdown-hours]');
      const minutesEl = countdownEl.querySelector('[data-countdown-minutes]');
      const secondsEl = countdownEl.querySelector('[data-countdown-seconds]');
      const ordersEl = countdownEl.querySelector('[data-countdown-orders]');
      const progressFillEl = countdownEl.querySelector('[data-countdown-progress-fill]');
      const expiredMessageEl = countdownEl.querySelector('[data-countdown-expired-message]');

      const targetTimestamp = parseTargetTimestamp(
        countdownEl.dataset.endDate,
        countdownEl.dataset.endTime
      );

      const expiredAction = (countdownEl.dataset.expiredAction || 'hide').toLowerCase();
      const ordersBase = toSafeNumber(countdownEl.dataset.ordersBase, 16);
      const ordersMax = toSafeNumber(countdownEl.dataset.ordersMax, 28);
      const progressMin = clamp(toSafeNumber(countdownEl.dataset.progressMin, 50), 0, 100);
      const progressMax = clamp(toSafeNumber(countdownEl.dataset.progressMax, 95), 0, 100);
      const rampHours = Math.max(1, toSafeNumber(countdownEl.dataset.rampHours, 15));
      const normalizedOrderMin = Math.min(ordersBase, ordersMax);
      const normalizedOrderMax = Math.max(ordersBase, ordersMax);
      const normalizedProgressMin = Math.min(progressMin, progressMax);
      const normalizedProgressMax = Math.max(progressMin, progressMax);

      const applyExpiredState = () => {
        if (expiredAction === 'show_message') {
          [daysEl, hoursEl, minutesEl, secondsEl].forEach((el) => {
            if (el) el.textContent = '00';
          });

          if (expiredMessageEl) {
            expiredMessageEl.hidden = false;
          }
          if (progressFillEl) {
            progressFillEl.style.width = '100%';
          }
          return;
        }

        countdownEl.hidden = true;
      };

      if (!targetTimestamp) {
        countdownEl.hidden = true;
        addCleanup(state, () => {
          delete countdownEl.dataset.productMainCountdownBound;
        });
        return;
      }

      countdownEl.hidden = false;

      const timer = createCountdownTimer(targetTimestamp, {
        onTick: (days, hours, minutes, seconds, diff) => {
          if (daysEl) daysEl.textContent = String(days).padStart(2, '0');
          if (hoursEl) hoursEl.textContent = String(hours).padStart(2, '0');
          if (minutesEl) minutesEl.textContent = String(minutes).padStart(2, '0');
          if (secondsEl) secondsEl.textContent = String(seconds).padStart(2, '0');

          const remainingHours = diff / (60 * 60 * 1000);
          const progressRatio = clamp(1 - (remainingHours / rampHours), 0, 1);
          const orderCount = Math.round(
            normalizedOrderMin + ((normalizedOrderMax - normalizedOrderMin) * progressRatio)
          );
          const progressPercent = normalizedProgressMin
            + ((normalizedProgressMax - normalizedProgressMin) * progressRatio);

          if (ordersEl) {
            ordersEl.textContent = String(orderCount);
          }
          if (progressFillEl) {
            progressFillEl.style.width = `${progressPercent.toFixed(2)}%`;
          }
          if (expiredMessageEl) {
            expiredMessageEl.hidden = true;
          }
        },
        onExpired: applyExpiredState
      });

      addCleanup(state, () => {
        if (timer) {
          timer.destroy();
        }
        delete countdownEl.dataset.productMainCountdownBound;
      });
    });
  }

  function initPresaleSection(section, state) {
    const presaleModules = Array.from(section.querySelectorAll('[data-presale-module]'));
    if (!presaleModules.length) return;

    const productData = getProductData(section, state);
    const variantSelect = section.querySelector('[data-variant-select]');
    const addToCartButton = section.querySelector('[data-add-to-cart]');
    const stickyAddButton = section.querySelector('[data-sticky-add]');
    if (addToCartButton && !addToCartButton.dataset.defaultLabel) {
      addToCartButton.dataset.defaultLabel = (addToCartButton.textContent || '').trim();
    }
    if (stickyAddButton && !stickyAddButton.dataset.defaultLabel) {
      stickyAddButton.dataset.defaultLabel = (stickyAddButton.textContent || '').trim();
    }

    const formEl = section.querySelector('[data-product-form] form') || section.querySelector('form[action*="/cart/add"]');
    const variantInput = formEl
      ? getAssociatedFormField(section, formEl, 'input[name="id"], select[name="id"]')
      : null;
    const hasVariantOptionRadios = Boolean(section.querySelector('[data-variant-option]'));

    const getCurrentVariant = () => {
      if (!productData || !Array.isArray(productData.variants) || !productData.variants.length) return null;
      const selectedVariantId = variantInput
        ? variantInput.value
        : (variantSelect ? variantSelect.value : productData.selected_variant_id);
      const numericVariantId = Number(selectedVariantId);

      return productData.variants.find((variant) => Number(variant.id) === numericVariantId) || productData.variants[0];
    };

    const getVariantFromOptionSelection = () => {
      if (!productData || !Array.isArray(productData.variants) || !productData.variants.length) return null;
      if (!hasVariantOptionRadios) {
        return getCurrentVariant();
      }

      const optionNames = Array.isArray(productData.option_names) ? productData.option_names : [];
      if (!optionNames.length) {
        return getCurrentVariant();
      }

      const selectedOptions = [];
      for (let index = 0; index < optionNames.length; index += 1) {
        const position = index + 1;
        const selectedRadio = section.querySelector(`[data-variant-option][data-option-position="${position}"]:checked`);
        if (!selectedRadio) return null;
        selectedOptions.push(selectedRadio.value);
      }

      return productData.variants.find((variant) => {
        return Array.isArray(variant.options)
          && variant.options.every((optionValue, optionIndex) => optionValue === selectedOptions[optionIndex]);
      }) || null;
    };

    presaleModules.forEach((presaleModule) => {
      if (!presaleModule || presaleModule.dataset.productMainPresaleBound === 'true') return;
      presaleModule.dataset.productMainPresaleBound = 'true';

      const addLabel = (addToCartButton && addToCartButton.dataset.defaultLabel)
        || (window.Hai && window.Hai.strings && window.Hai.strings.addToCart)
        || 'Add to cart';
      const stickyDefaultLabel = (stickyAddButton && stickyAddButton.dataset.defaultLabel)
        || section.dataset.addToBagLabel
        || addLabel;
      const soldOutLabel = (window.Hai && window.Hai.strings && window.Hai.strings.soldOut) || 'Sold out';
      const selectVariantLabel = (window.Hai && window.Hai.strings && window.Hai.strings.selectVariant) || 'Select a variant';
      const preOrderLabel = presaleModule.dataset.presaleButtonText
        || (window.Hai && window.Hai.strings && window.Hai.strings.preOrder)
        || 'Pre-order';
      const comingSoonLabel = presaleModule.dataset.presaleComingSoonText
        || (window.Hai && window.Hai.strings && window.Hai.strings.presaleComingSoon)
        || 'Coming Soon';
      const presaleEndedLabel = presaleModule.dataset.presaleEndedLabel
        || (window.Hai && window.Hai.strings && window.Hai.strings.presaleEnded)
        || 'Pre-sale Ended';

      const countdownEl = presaleModule.querySelector('[data-presale-countdown]');
      const countdownLabelEl = presaleModule.querySelector('[data-presale-countdown-label]');
      const daysEl = presaleModule.querySelector('[data-presale-days]');
      const hoursEl = presaleModule.querySelector('[data-presale-hours]');
      const minutesEl = presaleModule.querySelector('[data-presale-minutes]');
      const secondsEl = presaleModule.querySelector('[data-presale-seconds]');
      const badgeTextEl = presaleModule.querySelector('[data-presale-badge-text]');
      const shipDateEl = presaleModule.querySelector('[data-presale-ship-date]');
      const shipDateTextEl = presaleModule.querySelector('[data-presale-ship-date-text]');
      const noteEl = presaleModule.querySelector('[data-presale-note]');
      const notifyTrigger = presaleModule.querySelector('[data-notify-trigger]');

      let presaleInput = formEl ? formEl.querySelector('input[name="properties[_hai_presale]"]') : null;
      let presaleInputCreated = false;
      if (formEl && !presaleInput) {
        presaleInput = document.createElement('input');
        presaleInput.type = 'hidden';
        presaleInput.name = 'properties[_hai_presale]';
        presaleInput.value = '';
        formEl.appendChild(presaleInput);
        presaleInputCreated = true;
      }

      let countdownTimer = null;
      let countdownTarget = null;

      const clearCountdown = () => {
        if (countdownTimer) {
          countdownTimer.destroy();
          countdownTimer = null;
        }
        countdownTarget = null;
      };

      const getPresaleConfig = (variant) => {
        if (!variant) return null;

        return {
          enabled: variant.presale_enabled === true,
          startAt: variant.presale_start_at || '',
          endAt: variant.presale_end_at || '',
          shipText: variant.presale_ship_text || '',
          note: variant.presale_note || ''
        };
      };

      const getTimeStatus = (config) => {
        if (!config || !config.enabled) return 'regular';

        const now = Date.now();
        const nextStartTimestamp = config.startAt ? parseTargetTimestamp(config.startAt, '') : null;
        const nextEndTimestamp = config.endAt ? parseTargetTimestamp(config.endAt, '') : null;

        if (nextStartTimestamp && now < nextStartTimestamp) return 'coming_soon';
        if (nextEndTimestamp && now > nextEndTimestamp) return 'ended';
        return 'active';
      };

      const getPresaleStatus = (variant, config) => {
        const timeStatus = getTimeStatus(config);
        if (timeStatus !== 'active') return timeStatus;

        if (variant && variant.available === false && variant.inventory_policy !== 'continue') {
          return 'sold_out_guard';
        }

        return 'active';
      };

      const applyButtonState = (button, disabled, label) => {
        if (!button) return;
        button.disabled = disabled;
        button.setAttribute('aria-disabled', disabled ? 'true' : 'false');
        if (typeof label === 'string' && label.length) {
          button.textContent = label;
        }
      };

      const applyContentState = (config, status) => {
        const shipText = config ? String(config.shipText || '') : '';
        if (shipDateTextEl) {
          shipDateTextEl.textContent = shipText;
        }
        if (shipDateEl) {
          shipDateEl.hidden = !shipText || status === 'regular' || status === 'ended';
        }

        const noteText = config ? String(config.note || '') : '';
        if (noteEl) {
          noteEl.textContent = noteText;
          noteEl.hidden = !noteText || status === 'regular' || status === 'ended';
        }
      };

      const applyNotifyState = (status) => {
        if (!notifyTrigger) return;

        if (status === 'coming_soon') {
          notifyTrigger.hidden = false;
          notifyTrigger.disabled = false;
          notifyTrigger.dataset.notifyEventType = 'presale_start';
          notifyTrigger.textContent = (window.Hai && window.Hai.strings && window.Hai.strings.notifyOnSale)
            || 'Notify me when sale starts';
          return;
        }

        if (status === 'sold_out_guard') {
          notifyTrigger.hidden = false;
          notifyTrigger.disabled = false;
          notifyTrigger.dataset.notifyEventType = 'restock';
          notifyTrigger.textContent = (window.Hai && window.Hai.strings && window.Hai.strings.notifyMe)
            || 'Notify me';
          return;
        }

        notifyTrigger.hidden = true;
        notifyTrigger.dataset.notifyEventType = '';
      };

      const applyCountdownState = (status, config) => {
        if (!countdownEl) return;

        const targetTimestamp = status === 'coming_soon'
          ? parseTargetTimestamp(config && config.startAt ? config.startAt : '', '')
          : (status === 'active' ? parseTargetTimestamp(config && config.endAt ? config.endAt : '', '') : null);

        if (!targetTimestamp) {
          countdownEl.hidden = true;
          clearCountdown();
          return;
        }

        countdownEl.hidden = false;
        if (countdownLabelEl) {
          const startsIn = (window.Hai && window.Hai.strings && window.Hai.strings.presaleStartsIn) || 'Pre-sale starts in';
          const endsIn = (window.Hai && window.Hai.strings && window.Hai.strings.presaleEndsIn) || 'Pre-sale ends in';
          countdownLabelEl.textContent = status === 'coming_soon' ? startsIn : endsIn;
        }

        if (countdownTarget === targetTimestamp && countdownTimer) return;

        clearCountdown();
        countdownTarget = targetTimestamp;
        countdownTimer = createCountdownTimer(targetTimestamp, {
          onTick: (days, hours, minutes, seconds) => {
            if (daysEl) daysEl.textContent = String(days).padStart(2, '0');
            if (hoursEl) hoursEl.textContent = String(hours).padStart(2, '0');
            if (minutesEl) minutesEl.textContent = String(minutes).padStart(2, '0');
            if (secondsEl) secondsEl.textContent = String(seconds).padStart(2, '0');
          },
          onExpired: () => {
            clearCountdown();
            applyPresaleState();
          }
        });
      };

      const applyPresaleState = () => {
        const selectedVariant = getVariantFromOptionSelection();
        const hasValidSelection = !hasVariantOptionRadios || Boolean(selectedVariant);
        const variant = hasValidSelection ? selectedVariant : null;
        const config = hasValidSelection ? getPresaleConfig(variant) : null;
        const status = hasValidSelection ? getPresaleStatus(variant, config) : 'regular';

        presaleModule.dataset.presaleStatus = status;
        presaleModule.dataset.presaleStart = config && config.startAt ? config.startAt : '';
        presaleModule.dataset.presaleEnd = config && config.endAt ? config.endAt : '';
        presaleModule.hidden = status === 'regular' || status === 'ended';

        if (presaleInput) {
          presaleInput.value = (status === 'active' && hasValidSelection) ? '1' : '';
        }

        applyContentState(config, status);

        if (badgeTextEl) {
          if (status === 'coming_soon') {
            badgeTextEl.textContent = comingSoonLabel;
          } else if (status === 'active') {
            badgeTextEl.textContent = preOrderLabel;
          } else if (status === 'sold_out_guard') {
            badgeTextEl.textContent = soldOutLabel;
          } else {
            badgeTextEl.textContent = presaleEndedLabel;
          }
        }

        if (!hasValidSelection) {
          applyButtonState(addToCartButton, true, selectVariantLabel);
          applyButtonState(stickyAddButton, true, selectVariantLabel);
          applyNotifyState('invalid_selection');
          applyCountdownState(status, config);
          return;
        }

        if (status === 'coming_soon') {
          applyButtonState(addToCartButton, true, comingSoonLabel);
          applyButtonState(stickyAddButton, true, comingSoonLabel);
        } else if (status === 'active') {
          applyButtonState(addToCartButton, false, preOrderLabel);
          applyButtonState(stickyAddButton, false, preOrderLabel);
        } else if (status === 'sold_out_guard') {
          applyButtonState(addToCartButton, true, soldOutLabel);
          applyButtonState(stickyAddButton, true, soldOutLabel);
        } else if (!variant || variant.available === false) {
          applyButtonState(addToCartButton, true, soldOutLabel);
          applyButtonState(stickyAddButton, true, soldOutLabel);
        } else {
          applyButtonState(addToCartButton, false, addLabel);
          applyButtonState(stickyAddButton, false, stickyDefaultLabel);
        }

        applyNotifyState(status);
        applyCountdownState(status, config);
      };

      const onUiUpdated = () => {
        applyPresaleState();
      };

      applyPresaleState();
      section.addEventListener('hai:product:ui-updated', onUiUpdated);

      addCleanup(state, () => {
        clearCountdown();
        section.removeEventListener('hai:product:ui-updated', onUiUpdated);

        if (presaleInputCreated && presaleInput && presaleInput.parentNode) {
          presaleInput.parentNode.removeChild(presaleInput);
        }

        delete presaleModule.dataset.productMainPresaleBound;
      });
    });
  }

  function initNotifySection(section, state) {
    const presaleModules = Array.from(section.querySelectorAll('[data-presale-module]'));
    if (!presaleModules.length) return;

    const bodyLockClass = 'hai-presale-overflow-hidden';
    const syncBodyLock = () => {
      const hasOpenModal = Boolean(document.querySelector('[data-notify-modal]:not([hidden])'));
      document.body.classList.toggle(bodyLockClass, hasOpenModal);
    };

    const sectionForm = section.querySelector('[data-product-form] form') || section.querySelector('form[action*="/cart/add"]');
    const variantField = sectionForm
      ? getAssociatedFormField(section, sectionForm, 'input[name="id"], select[name="id"]')
      : null;

    presaleModules.forEach((presaleModule) => {
      if (!presaleModule || presaleModule.dataset.productMainNotifyBound === 'true') return;

      const trigger = presaleModule.querySelector('[data-notify-trigger]');
      const modal = presaleModule.querySelector('[data-notify-modal]');
      const closeButtons = modal ? Array.from(modal.querySelectorAll('[data-notify-close]')) : [];
      const form = modal ? modal.querySelector('[data-notify-form]') : null;
      const messageEl = modal ? modal.querySelector('[data-notify-message]') : null;
      const submitButton = modal ? modal.querySelector('[data-notify-submit]') : null;
      if (!trigger || !modal || !form) return;

      presaleModule.dataset.productMainNotifyBound = 'true';

      const setMessage = (message, isError) => {
        if (!messageEl) return;
        if (!message) {
          messageEl.hidden = true;
          messageEl.textContent = '';
          messageEl.classList.remove('is-error');
          return;
        }
        messageEl.hidden = false;
        messageEl.textContent = message;
        messageEl.classList.toggle('is-error', Boolean(isError));
      };

      let isKeydownBound = false;
      const bindEscapeListener = () => {
        if (isKeydownBound) return;
        document.addEventListener('keydown', onDocumentKeydown);
        isKeydownBound = true;
      };
      const unbindEscapeListener = () => {
        if (!isKeydownBound) return;
        document.removeEventListener('keydown', onDocumentKeydown);
        isKeydownBound = false;
      };

      const openModal = () => {
        if (trigger.hidden) return;
        modal.hidden = false;
        setMessage('', false);
        bindEscapeListener();
        syncBodyLock();
      };

      const closeModal = () => {
        modal.hidden = true;
        unbindEscapeListener();
        syncBodyLock();
      };

      const onTriggerClick = (event) => {
        event.preventDefault();
        openModal();
      };

      const onOverlayClick = (event) => {
        if (event.target === modal) {
          closeModal();
        }
      };

      const onDocumentKeydown = (event) => {
        if (event.key === 'Escape' && !modal.hidden) {
          closeModal();
        }
      };

      const onCloseClick = (event) => {
        event.preventDefault();
        closeModal();
      };

      const onSubmit = async (event) => {
        event.preventDefault();

        const endpoint = presaleModule.dataset.notifyEndpoint || '';
        const eventType = trigger.dataset.notifyEventType || 'restock';
        const formData = new FormData(form);
        const email = String(formData.get('email') || '').trim();
        const name = String(formData.get('name') || '').trim();
        const phone = String(formData.get('phone') || '').trim();
        const location = String(formData.get('location') || '').trim();
        const variantId = variantField ? String(variantField.value || '') : '';
        const productId = section.dataset.productId || '';

        if (!email) {
          setMessage(
            (window.Hai && window.Hai.strings && window.Hai.strings.notifyEmailRequired) || 'Please enter your email.',
            true
          );
          return;
        }

        if (!endpoint) {
          setMessage(
            (window.Hai && window.Hai.strings && window.Hai.strings.notifyNoEndpoint) || 'Notify endpoint is not configured.',
            true
          );
          return;
        }

        const payload = {
          product_id: productId,
          variant_id: variantId,
          event_type: eventType,
          email,
          name,
          phone,
          location,
          locale: (window.Hai && window.Hai.settings && window.Hai.settings.locale) || ''
        };

        if (submitButton) {
          submitButton.disabled = true;
        }

        try {
          const response = await fetch(endpoint, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            credentials: 'same-origin',
            body: JSON.stringify(payload)
          });

          if (!response.ok) {
            throw new Error(`Notify request failed with ${response.status}`);
          }

          setMessage(
            (window.Hai && window.Hai.strings && window.Hai.strings.notifySuccess)
            || 'Thanks! We will notify you when available.',
            false
          );
          form.reset();
        } catch (error) {
          setMessage(
            (window.Hai && window.Hai.strings && window.Hai.strings.notifyError)
            || 'Unable to submit now, please try again later.',
            true
          );
        } finally {
          if (submitButton) {
            submitButton.disabled = false;
          }
        }
      };

      trigger.addEventListener('click', onTriggerClick);
      modal.addEventListener('click', onOverlayClick);
      closeButtons.forEach((button) => {
        button.addEventListener('click', onCloseClick);
      });
      form.addEventListener('submit', onSubmit);

      addCleanup(state, () => {
        trigger.removeEventListener('click', onTriggerClick);
        modal.removeEventListener('click', onOverlayClick);
        unbindEscapeListener();
        closeButtons.forEach((button) => {
          button.removeEventListener('click', onCloseClick);
        });
        form.removeEventListener('submit', onSubmit);

        closeModal();
        delete presaleModule.dataset.productMainNotifyBound;
      });
    });
  }

  function initProductSalesBlocks(section, state) {
    const productData = getProductData(section, state);
    const variantField = section.querySelector('[data-variant-select], input[name="id"], select[name="id"]');
    const mainAddButton = section.querySelector('[data-add-to-cart]');
    const salesAddButtons = Array.from(section.querySelectorAll('[data-sales-add-to-cart]'));

    const syncSalesAddButtonState = () => {
      const isDisabled = !mainAddButton
        || mainAddButton.disabled
        || mainAddButton.getAttribute('aria-disabled') === 'true';

      salesAddButtons.forEach((button) => {
        button.disabled = isDisabled;
        button.setAttribute('aria-disabled', String(isDisabled));
      });
    };

    const onSalesAddButtonClick = (event) => {
      if (!mainAddButton || mainAddButton.disabled || mainAddButton.getAttribute('aria-disabled') === 'true') {
        event.preventDefault();
        return;
      }

      mainAddButton.click();
    };

    salesAddButtons.forEach((button) => {
      button.addEventListener('click', onSalesAddButtonClick);
    });

    let addButtonObserver = null;
    if (mainAddButton && salesAddButtons.length && typeof MutationObserver === 'function') {
      addButtonObserver = new MutationObserver(syncSalesAddButtonState);
      addButtonObserver.observe(mainAddButton, {
        attributes: true,
        attributeFilter: ['disabled', 'aria-disabled']
      });
    }

    syncSalesAddButtonState();
    section.addEventListener('hai:product:ui-updated', syncSalesAddButtonState);
    addCleanup(state, () => {
      section.removeEventListener('hai:product:ui-updated', syncSalesAddButtonState);
      salesAddButtons.forEach((button) => {
        button.removeEventListener('click', onSalesAddButtonClick);
      });
      if (addButtonObserver) addButtonObserver.disconnect();
    });

    const getCurrentVariant = () => {
      if (!productData || !Array.isArray(productData.variants)) return null;
      const selectedId = Number(variantField ? variantField.value : productData.selected_variant_id);
      return productData.variants.find((variant) => Number(variant.id) === selectedId)
        || productData.variants[0]
        || null;
    };

    // 价格模块只读取主产品当前变体，避免在右栏另建第二套产品状态。
    const updatePriceBlocks = () => {
      const variant = getCurrentVariant();
      if (!variant) return;

      section.querySelectorAll('[data-hai-product-price-ladder][data-use-product-price="true"]').forEach((root) => {
        const output = root.querySelector('[data-price-ladder-current]');
        if (output) output.textContent = formatMoney(variant.price);
      });

      section.querySelectorAll('[data-hai-product-value-stack][data-use-product-price="true"]').forEach((root) => {
        const autoTotalValue = root.dataset.autoTotalValue === 'true';
        const addonValue = Number(root.dataset.addonValueCents) || 0;
        const totalValue = autoTotalValue
          ? Number(variant.price || 0) + addonValue
          : Number(root.dataset.totalValueCents) || 0;
        const primaryItemOutput = root.querySelector('[data-value-stack-primary-item]');
        const totalOutput = root.querySelector('[data-value-stack-total]');
        const todayOutput = root.querySelector('[data-value-stack-today]');
        const savingOutput = root.querySelector('[data-value-stack-saving]');
        const saving = Math.max(0, totalValue - Number(variant.price || 0));

        root.dataset.totalValueCents = String(totalValue);
        if (primaryItemOutput) primaryItemOutput.textContent = formatMoney(variant.price);
        if (totalOutput) totalOutput.textContent = formatMoney(totalValue);
        if (todayOutput) todayOutput.textContent = formatMoney(variant.price);
        if (!savingOutput) return;

        if (saving <= 0 || totalValue <= 0) {
          savingOutput.hidden = true;
          return;
        }

        const percent = Math.round((saving / totalValue) * 100);
        savingOutput.textContent = String(root.dataset.savingTemplate || '')
          .replace('{amt}', formatMoney(saving))
          .replace('{pct}', `${percent}%`);
        savingOutput.hidden = false;
      });
    };

    updatePriceBlocks();
    section.addEventListener('hai:product:ui-updated', updatePriceBlocks);
    addCleanup(state, () => {
      section.removeEventListener('hai:product:ui-updated', updatePriceBlocks);
    });

    section.querySelectorAll('[data-hai-product-price-ladder][data-countdown-target]').forEach((root) => {
      if (root.hidden) return;
      const output = root.querySelector('[data-price-ladder-countdown]');
      const target = new Date(root.dataset.countdownTarget || '').getTime();
      if (!output || !Number.isFinite(target)) return;

      const timer = createCountdownTimer(target, {
        onTick: (days, hours, minutes, seconds) => {
          const totalHours = (days * 24) + hours;
          output.textContent = [totalHours, minutes, seconds]
            .map((value) => String(value).padStart(2, '0'))
            .join(':');
        },
        onExpired: () => {
          output.textContent = '00:00:00';
        }
      });
      if (timer) addCleanup(state, () => timer.destroy());
    });

    section.querySelectorAll('[data-hai-product-live-orders]').forEach((root) => {
      if (root.hidden) return;
      const list = root.querySelector('[data-live-orders-list]');
      const viewport = root.querySelector('[data-live-orders-viewport]');
      const message = root.querySelector('[data-live-orders-message]');
      const mode = String(root.dataset.mode || 'aggregate');
      const endpoint = String(root.dataset.endpoint || '').trim();
      const refreshMs = Math.max(30, Number(root.dataset.refresh) || 120) * 1000;
      const maxItems = Math.max(1, Number(root.dataset.maxItems) || 5);
      const visibleRows = Math.max(1, Number(root.dataset.visibleRows) || 3);
      const tickerMs = Math.max(2, Number(root.dataset.tickerSeconds) || 3) * 1000;
      const viewportBottomSpace = 24;
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      let activeController = null;
      let refreshTimer = null;
      let tickerTimer = null;
      let tickerResetTimer = null;
      let tickerPaused = false;
      let resizeObserver = null;

      const showMessage = (text) => {
        if (!message) return;
        message.textContent = text;
        message.hidden = false;
      };

      const hideMessage = () => {
        if (message) message.hidden = true;
      };

      const syncViewportHeight = () => {
        if (!list || !viewport || !list.children.length) return;
        const rows = Array.from(list.children).slice(0, visibleRows);
        const gap = Number.parseFloat(window.getComputedStyle(list).rowGap) || 0;
        const height = rows.reduce((total, row) => total + row.getBoundingClientRect().height, 0)
          + (gap * Math.max(0, rows.length - 1))
          + viewportBottomSpace;
        viewport.style.height = `${Math.ceil(height)}px`;
      };

      const rotateOrders = () => {
        if (!list || list.children.length <= visibleRows || tickerPaused || document.hidden || list.dataset.tickerBusy === '1') return;
        const first = list.firstElementChild;
        const gap = Number.parseFloat(window.getComputedStyle(list).rowGap) || 0;
        const shift = first.getBoundingClientRect().height + gap;
        list.dataset.tickerBusy = '1';
        list.classList.add('is-ticker-moving');
        list.style.transform = `translateY(-${shift}px)`;
        tickerResetTimer = window.setTimeout(() => {
          list.classList.remove('is-ticker-moving');
          list.appendChild(first);
          list.style.transform = 'translateY(0)';
          list.dataset.tickerBusy = '0';
          syncViewportHeight();
        }, 580);
      };

      const startTicker = () => {
        if (reduceMotion || tickerTimer || !list || list.children.length <= visibleRows) return;
        tickerTimer = window.setInterval(rotateOrders, tickerMs);
      };

      const makeLocationIcon = () => {
        const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
        svg.setAttribute('viewBox', '0 0 24 24');
        svg.setAttribute('aria-hidden', 'true');
        path.setAttribute('d', 'M20 10c0 6-8 12-8 12s-8-6-8-10a8 8 0 0 1 16 0Z');
        circle.setAttribute('cx', '12');
        circle.setAttribute('cy', '10');
        circle.setAttribute('r', '3');
        svg.append(path, circle);
        return svg;
      };

      const renderOrders = (orders) => {
        if (!list) return;
        list.replaceChildren();
        orders.slice(0, maxItems).forEach((order) => {
          const row = document.createElement('li');
          const copy = document.createElement('span');
          const copyText = document.createElement('span');
          const time = document.createElement('time');
          const customer = order.customer_initial || order.customer || order.name || '';
          const city = order.city || order.location || '';
          const item = order.item_title || order.product || order.item || '';
          const quantity = Number(order.quantity) > 1 ? ` ×${Number(order.quantity)}` : '';
          copy.className = 'hai-product-sales__order-copy';
          copyText.textContent = [customer, city].filter(Boolean).join(', ')
            + ([customer, city].some(Boolean) && item ? ' · ' : '')
            + item
            + quantity;
          time.textContent = order.time_text || order.time || '';
          copy.append(makeLocationIcon(), copyText);
          row.append(copy, time);
          list.append(row);
        });
        syncViewportHeight();
        startTicker();
      };

      // App Proxy 必须使用当前店铺同源的 /apps/ 路径，阻断把买家数据发往任意外部地址。
      const loadOrders = async () => {
        if (!endpoint) {
          showMessage(root.dataset.emptyMessage || 'No recent orders returned.');
          return;
        }

        const endpointUrl = new URL(endpoint, window.location.origin);
        if (endpointUrl.origin !== window.location.origin || !endpointUrl.pathname.startsWith('/apps/')) {
          showMessage(root.dataset.errorMessage || 'Recent order data is temporarily unavailable.');
          return;
        }

        if (activeController) activeController.abort();
        activeController = new AbortController();

        try {
          const response = await fetch(endpointUrl.toString(), {
            credentials: 'same-origin',
            headers: { Accept: 'application/json' },
            signal: activeController.signal
          });
          if (!response.ok) throw new Error(`Live orders request failed with ${response.status}`);
          const payload = await response.json();
          const orders = Array.isArray(payload) ? payload : (payload.orders || payload.items || []);
          if (!Array.isArray(orders) || !orders.length) {
            if (list) list.replaceChildren();
            showMessage(root.dataset.emptyMessage || 'No recent orders returned.');
            return;
          }
          renderOrders(orders);
          hideMessage();
        } catch (error) {
          if (error && error.name === 'AbortError') return;
          showMessage(root.dataset.errorMessage || 'Recent order data is temporarily unavailable.');
        }
      };

      const pauseTicker = () => { tickerPaused = true; };
      const resumeTicker = () => { tickerPaused = false; };
      root.addEventListener('mouseenter', pauseTicker);
      root.addEventListener('mouseleave', resumeTicker);
      root.addEventListener('focusin', pauseTicker);
      root.addEventListener('focusout', resumeTicker);

      if ('ResizeObserver' in window && viewport) {
        resizeObserver = new ResizeObserver(syncViewportHeight);
        resizeObserver.observe(list);
      }

      syncViewportHeight();
      startTicker();

      if (mode === 'feed') {
        loadOrders();
        refreshTimer = window.setInterval(loadOrders, refreshMs);
      }
      addCleanup(state, () => {
        if (activeController) activeController.abort();
        if (refreshTimer) window.clearInterval(refreshTimer);
        if (tickerTimer) window.clearInterval(tickerTimer);
        if (tickerResetTimer) window.clearTimeout(tickerResetTimer);
        if (resizeObserver) resizeObserver.disconnect();
        root.removeEventListener('mouseenter', pauseTicker);
        root.removeEventListener('mouseleave', resumeTicker);
        root.removeEventListener('focusin', pauseTicker);
        root.removeEventListener('focusout', resumeTicker);
      });
    });
  }

  function initSection(section) {
    if (!section || !section.matches(SECTION_SELECTOR) || sectionStateMap.has(section)) return;

    const state = { cleanups: [] };
    sectionStateMap.set(section, state);

    try {
      initGallerySection(section, state);
      initVariantSection(section, state);
      initProductSalesBlocks(section, state);
      initBundleSection(section, state);
      bindProductSubmit(section, state);
      initHaiAddonSection(section, state);
      initProductGiftSection(section, state);
      initGiftCustomizerSection(section, state);
      initStickySection(section, state);
      initPromoSection(section, state);
      initFaqBlocksSection(section, state);
      initSalesCountdownSection(section, state);
      initPresaleSection(section, state);
      initNotifySection(section, state);
    } catch (error) {
      console.error('Product main init error:', error);
    }
  }

  function destroySection(section) {
    const state = sectionStateMap.get(section);
    if (!state) return;

    state.cleanups.forEach((cleanup) => {
      try {
        cleanup();
      } catch (error) {
        console.error('Product main cleanup error:', error);
      }
    });

    sectionStateMap.delete(section);
  }

  /**
   * 统一旧市场模板中的兼容性文案。
   * 部分 Shopify Markets 上下文仍会输出旧区块数据，因此在产品脚本加载时
   * 将旧的 LEGO 品牌表述迁移为当前站点使用的通用表述。
   */
  function normalizeProductSpecsCompatibility(root) {
    const target = root || document;
    if (!target || typeof target.querySelectorAll !== 'function') return;

    target.querySelectorAll('.specs-accordion__item').forEach((item) => {
      const heading = item.querySelector('.specs-accordion__item-heading');
      const content = item.querySelector('.specs-accordion__item-content');
      if (!heading || !content) return;

      const headingText = heading.textContent.trim();
      if (headingText !== 'LEGO Compatibility' && headingText !== 'Compatibility') return;

      heading.textContent = 'Compatibility';
      if (content.textContent.trim() === 'Compatible') {
        const paragraph = content.querySelector('p');
        if (paragraph) {
          paragraph.textContent = 'Yes';
        } else {
          content.textContent = 'Yes';
        }

      }
    });
  }

  function initInRoot(root) {
    const target = root || document;
    if (!target) return;

    normalizeProductSpecsCompatibility(target);

    if (typeof target.matches === 'function' && target.matches(SECTION_SELECTOR)) {
      initSection(target);
    }

    if (typeof target.querySelectorAll === 'function') {
      target.querySelectorAll(SECTION_SELECTOR).forEach((section) => {
        initSection(section);
      });
    }
  }

  function destroyInRoot(root) {
    const target = root || document;
    if (!target) return;

    if (typeof target.matches === 'function' && target.matches(SECTION_SELECTOR)) {
      destroySection(target);
    }

    if (typeof target.querySelectorAll === 'function') {
      target.querySelectorAll(SECTION_SELECTOR).forEach((section) => {
        destroySection(section);
      });
    }
  }

  function bindLifecycleEvents() {
    document.addEventListener('shopify:section:load', (event) => {
      initInRoot(event.target);
    });

    document.addEventListener('shopify:section:unload', (event) => {
      destroyInRoot(event.target);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initInRoot(document);
      bindLifecycleEvents();
    });
  } else {
    initInRoot(document);
    bindLifecycleEvents();
  }

  window.HaiProductMain = {
    init: initInRoot,
    destroy: destroyInRoot
  };
})();
